import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Alert, BackHandler, Image, Linking, Platform, RefreshControl, SafeAreaView, ScrollView, StatusBar, Switch, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { launchImageLibrary } from 'react-native-image-picker';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { getStoredToken, setStoredToken } from './src/auth/session';
import { loadGuestLanguagePreference, saveGuestLanguagePreference } from './src/auth/language-preference';
import { apiGet, apiPatch, apiPost, apiUploadAvatar, authenticate as authenticateRequest, checkApiHealth, fetchBootstrap, setUnauthorizedHandler, subscribeApiAvailability } from './src/api/client';
import {
  Avatar,
  BottomTabs,
  Header,
  NotificationRow,
  Performance,
  OutlinePill,
  PillRow,
  PremiumPill,
  QuickAction,
  ScoreStepper,
  ScreenTitleRow,
  SectionHeader,
  SettingsRow,
  StatGrid,
} from './src/components/ui';
import { seed } from './src/data/seed';
import { dictionary } from './src/i18n';
import { detectSystemLanguageTag, getInitialAppLanguage, normalizeLanguage } from './src/i18n-locale';
import { formatMessage } from './src/i18n-format';
import { authScreens } from './src/navigation';
import { applyWebRoute, loggedOutEntryScreen, type AppNavigationSnapshot, type WebRoute } from './src/navigation/web';
import { shareContent } from './src/platform/share';
import { buildNotificationFeed, buildNotificationHistoryFeed, getHomeNotificationPreview, getNotificationCount, getUnreadNotificationCount, handleNotificationPress, markNotificationItemRead, type NotificationCopy, type NotificationFeedItem } from './src/notifications';
import type { AppNotification } from './src/types';
import { CreateAccountScreen, LandingScreen, LegalPageScreen, WelcomeScreen } from './src/screens/AuthScreens';
import { SplashScreen } from './src/screens/SplashScreen';
import { alertMessage, confirmAction } from './src/platform/confirm';
import { hideDocumentSplash, waitForDocumentFonts } from './src/platform/document-splash';
import { scrollDocumentToTop } from './src/platform/scroll-to-top';
import { applyAppUpdate, canApplyAppUpdate, startAppUpdateWatcher } from './src/platform/app-update';
import { LegalFooter, PRODUCT_NAME } from './src/components/legal';
import { ProfileQrCode } from './src/components/ProfileQrCode';
import { InviteFriendCard } from './src/components/InviteFriendCard';
import { FeaturedEventCard } from './src/components/FeaturedEventCard';
import { FeaturedEventVideo } from './src/components/FeaturedEventVideo';
import { AppStatusBanner } from './src/components/AppStatusBanner';
import { PwaInstallInstructions } from './src/components/PwaInstallInstructions';
import { filterFriendDirectory } from './src/friends-directory';
import { buildMatchHoles, matchHoleLabel, resolveMatchHoleCount } from './src/match-holes';
import {
  buildHoleScoreDrafts,
  coverageComplete,
  displayedStrokes,
  firstUnscoredHoleIndex,
  hasReporterEntries,
  mergeLiveScoreDrafts,
  reportsFromLegacyHoleStats,
  savedHoleScoreLabel,
} from './src/score-state';
import { fallbackHandleFromPlayer, profileHandleLabel, profileUrl } from './src/profile';
import { courseHandicapLabel, formatCourseHandicap, formatHandicapIndex, resolveHandicapIndex, type HandicapFields } from './src/handicap';
import { formatPlayerRecord, formatRatingChange, matchWinsLosses, type PlayerRecordFields } from './src/player-stats';
import { colors, styles } from './src/theme';
import type { ActiveMatch, BootstrapData, Course, Language, Player, Screen, Tab } from './src/types';

export type { AppNavigationSnapshot };

type AppProps = {
  webRoute?: WebRoute;
  profileRouteData?: any;
  profileRouteLoading?: boolean;
  onNavigate?: (snapshot: AppNavigationSnapshot) => void;
};

function App({
  webRoute,
  profileRouteData,
  profileRouteLoading,
  onNavigate,
}: AppProps = {}) {
  const initialRoute = webRoute ? applyWebRoute(webRoute, Boolean(getStoredToken())) : undefined;
  const [language, setLanguage] = useState<Language>(() => getInitialAppLanguage(loadGuestLanguagePreference()));
  const [screen, setScreen] = useState<Screen>(initialRoute?.screen ?? (getStoredToken() ? 'main' : loggedOutEntryScreen()));
  const [history, setHistory] = useState<Screen[]>([]);
  const [tab, setTab] = useState<Tab>(initialRoute?.tab ?? 'home');
  const [data, setData] = useState<BootstrapData>(seed);
  const [token, setToken] = useState<string | undefined>(() => getStoredToken());
  const [completedMatch, setCompletedMatch] = useState<ActiveMatch | null>(null);
  const [historyMatch, setHistoryMatch] = useState<ActiveMatch | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<any>();
  const [pendingOpponentId, setPendingOpponentId] = useState<string>();
  const [refreshing, setRefreshing] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const [appUpdateAvailable, setAppUpdateAvailable] = useState(false);
  const skipNextNavigationSync = useRef(false);
  const skipInitialTokenBootstrap = useRef(Boolean(token));
  const scrollRef = useRef<ScrollView>(null);
  const dataRef = useRef(data);
  const screenRef = useRef(screen);
  const apiAvailableRef = useRef(true);
  dataRef.current = data;
  screenRef.current = screen;
  apiAvailableRef.current = !serviceUnavailable;
  const t = dictionary[language];

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      try {
        if (token) {
          await loadBootstrapData(token);
        }
        await waitForDocumentFonts();
      } finally {
        if (!cancelled) {
          setShowSplash(false);
        }
      }
    }

    void boot();
    return () => {
      cancelled = true;
    };
    // Initial launch only — later token changes use the bootstrap effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showSplash) {
      return;
    }
    hideDocumentSplash();
  }, [showSplash]);

  useEffect(() => {
    return subscribeApiAvailability(available => {
      setServiceUnavailable(!available);
    });
  }, []);

  useEffect(() => {
    return startAppUpdateWatcher({
      getCanApplyUpdate: () => canApplyAppUpdate(screenRef.current) && apiAvailableRef.current,
      onUpdateAvailable: () => setAppUpdateAvailable(true),
    });
  }, []);

  useEffect(() => {
    if (appUpdateAvailable && canApplyAppUpdate(screen) && !serviceUnavailable) {
      applyAppUpdate();
    }
  }, [appUpdateAvailable, screen, serviceUnavailable]);

  useEffect(() => {
    if (!serviceUnavailable) {
      return;
    }
    let cancelled = false;
    async function retry() {
      const ok = await checkApiHealth();
      if (ok && !cancelled && token) {
        await loadBootstrapData(token);
      }
    }
    void retry();
    const interval = setInterval(() => {
      void retry();
    }, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [serviceUnavailable, token]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setToken(undefined);
      setStoredToken(undefined);
      setHistory([]);
      setTab('home');
      setScreen(loggedOutEntryScreen());
    });
    return () => setUnauthorizedHandler(undefined);
  }, []);

  useEffect(() => {
    if (!token) {
      skipInitialTokenBootstrap.current = false;
      return;
    }
    if (skipInitialTokenBootstrap.current) {
      skipInitialTokenBootstrap.current = false;
      return;
    }
    void loadBootstrapData(token);
  }, [token]);

  useEffect(() => {
    setStoredToken(token);
  }, [token]);

  useEffect(() => {
    if (!webRoute) {
      return;
    }
    const next = applyWebRoute(webRoute, Boolean(token));
    skipNextNavigationSync.current = true;
    setScreen(next.screen);
    if (webRoute.kind !== 'profile') {
      setTab(next.tab);
      if (next.screen !== 'userProfile') {
        setHistory([]);
      }
    }
  }, [webRoute, token]);

  useEffect(() => {
    if (webRoute?.kind === 'profile') {
      if (profileRouteData) {
        setSelectedProfile(profileRouteData);
      }
      if (profileRouteData === null) {
        setSelectedProfile(undefined);
      }
    }
  }, [profileRouteData, webRoute]);

  useEffect(() => {
    function resetScroll() {
      scrollRef.current?.scrollTo({ y: 0, animated: false });
      scrollDocumentToTop();
    }
    resetScroll();
    const frame = requestAnimationFrame(resetScroll);
    return () => cancelAnimationFrame(frame);
  }, [screen, tab]);

  useEffect(() => {
    if (!onNavigate) {
      return;
    }
    if (skipNextNavigationSync.current) {
      skipNextNavigationSync.current = false;
      return;
    }
    onNavigate({
      screen,
      tab,
      profileHandle: screen === 'userProfile' ? selectedProfile?.handle : undefined,
    });
  }, [onNavigate, screen, tab, selectedProfile]);

  const goBack = useCallback(() => {
    if (history.length > 0) {
      const previousScreen = history[history.length - 1];
      setHistory(previous => previous.slice(0, -1));
      setScreen(previousScreen);
      return true;
    }

    if (screen !== 'main' && !authScreens.includes(screen)) {
      if (screen === 'findMatch' || screen === 'scoreInput') {
        setTab('home');
      }
      setScreen('main');
      return true;
    }

    return false;
  }, [history, screen]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      return undefined;
    }
    const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
      if (goBack()) {
        return true;
      }
      return false;
    });

    return () => subscription.remove();
  }, [goBack]);

  useEffect(() => {
    if (!token || authScreens.includes(screen)) {
      return;
    }
    const intervalMs = screen === 'scoreInput' ? 3000 : 8000;
    const interval = setInterval(() => {
      loadBootstrapData(token);
    }, intervalMs);
    return () => clearInterval(interval);
  }, [screen, token]);

  async function authenticateUser(path: string, body: object) {
    const result = await authenticateRequest(path, body);
    if (typeof result?.token === 'string') {
      setToken(result.token);
    }
    return result;
  }

  async function refreshBootstrap() {
    return loadBootstrapData(token);
  }

  async function loadBootstrapData(authToken?: string) {
    setRefreshing(true);
    try {
      const next = await fetchBootstrap(authToken);
      setData(next);
      if (authToken && next.player.preferredLanguage) {
        const profileLanguage = normalizeLanguage(next.player.preferredLanguage);
        setLanguage(profileLanguage);
        saveGuestLanguagePreference(profileLanguage);
      }
      return next;
    } catch {
      return dataRef.current;
    } finally {
      setRefreshing(false);
    }
  }

  const setAppLanguage = useCallback(async (next: Language) => {
    setLanguage(next);
    saveGuestLanguagePreference(next);
    if (token) {
      await apiPatch('/users/me/preferences', { preferredLanguage: next }, token);
    }
  }, [token]);

  function notify(message: string) {
    alertMessage(t.appName, message);
  }

  async function action(path: string, payload: object, message: string) {
    await apiPost(path, payload, token);
    notify(message);
  }

  async function actionAndRefresh(path: string, payload: object, message: string) {
    await apiPost(path, payload, token);
    await loadBootstrapData(token);
    notify(message);
  }

  async function handleNotificationTap(item: NotificationFeedItem, pressHandlers: Parameters<typeof handleNotificationPress>[1]) {
    if (token) {
      await markNotificationItemRead(item, path => apiPost(path, {}, token));
      await loadBootstrapData(token);
    }
    handleNotificationPress(item, pressHandlers);
  }

  async function handleNotificationDismiss(item: NotificationFeedItem) {
    if (!token || item.read) {
      return;
    }
    await markNotificationItemRead(item, path => apiPost(path, {}, token));
    await loadBootstrapData(token);
  }

  async function handleReadAllNotifications() {
    if (!token) {
      return;
    }
    await apiPost('/notifications/read-all', {}, token);
    await loadBootstrapData(token);
  }

  function redirectIfLoggedOut() {
    if (token) {
      return false;
    }
    setHistory([]);
    setTab('home');
    setScreen(loggedOutEntryScreen());
    return true;
  }

  async function openScreen(nextScreen: Screen) {
    if (redirectIfLoggedOut()) {
      return;
    }
    if (token && ['friends', 'findMatch', 'matchplaySetup', 'communities', 'events', 'golfClub', 'academy', 'tournament', 'scoreInput', 'matchHistory'].includes(nextScreen)) {
      await loadBootstrapData(token);
    }
    navigateTo(nextScreen);
  }

  async function openActiveMatch() {
    if (!token) {
      notify(t.signInToContinueMatch);
      return;
    }
    const next = await loadBootstrapData(token);
    if (!next.activeMatch) {
      notify(t.noActiveMatchFound);
      return;
    }
    navigateTo('scoreInput');
  }

  async function showMatchDetail(matchId: string) {
    try {
      setHistoryMatch(await apiGet(`/matchplays/${matchId}`, token));
      navigateTo('matchHistory');
    } catch {
      notify(t.couldNotLoadMatchHistory);
    }
  }

  async function showMatchResult(matchId: string) {
    try {
      const match = await apiGet(`/matchplays/${matchId}`, token);
      if (match?.status === 'completed') {
        setCompletedMatch(match);
        navigateTo('matchResult');
        return;
      }
      await showMatchDetail(matchId);
    } catch {
      notify(t.couldNotLoadMatchResult);
    }
  }

  async function cancelActiveMatch(matchId?: string) {
    const id = matchId ?? data.activeMatch?.id;
    if (!id || !token) {
      notify(t.noActiveMatchToCancel);
      return;
    }
    const confirmed = await confirmAction({
      title: t.cancelMatchTitle,
      message: t.cancelMatchBody,
      confirmLabel: t.cancelMatchAction,
      cancelLabel: t.keepPlaying,
    });
    if (!confirmed) {
      return;
    }
    try {
      const result = await apiPost(`/matchplays/${id}/cancel`, {}, token);
      if (!result?.cancelled) {
        notify(t.couldNotCancelMatch);
        return;
      }
      setCompletedMatch(null);
      await loadBootstrapData(token);
      setHistory([]);
      setTab('home');
      navigateTo('main');
      notify(t.matchCancelled);
    } catch (error: any) {
      notify(error?.message ?? t.couldNotCancelMatch);
    }
  }

  async function handleMatchCancelled() {
    await loadBootstrapData(token);
    setHistory([]);
    setTab('home');
    navigateTo('main');
    notify(t.matchWasCancelled);
  }

  function openCourseSelect(opponentId?: string) {
    if (data.activeMatch) {
      notify(t.activeMatchExistsContinue);
      openActiveMatch();
      return;
    }
    if (!opponentId && !(data.friends?.length)) {
      notify(t.addFriendFirst);
      return;
    }
    setPendingOpponentId(opponentId);
    navigateTo('selectCourse');
  }

  async function startMatchplay(courseId: string, teeId: string, holesToPlay: 9 | 18, opponentId?: string) {
    if (data.activeMatch) {
      notify(t.activeMatchExistsContinue);
      await openActiveMatch();
      return;
    }
    const resolvedOpponentId = opponentId ?? pendingOpponentId ?? data.friends?.[0]?.id;
    const opponent =
      data.friends?.find(friend => friend.id === resolvedOpponentId) ??
      data.appUsers?.find(user => user.id === resolvedOpponentId);
    if (!resolvedOpponentId || !opponent) {
      notify(t.selectOpponentFirst);
      navigateTo('findMatch');
      return;
    }
    const course = data.courses?.find(item => item.id === courseId);
    if (!course) {
      notify(t.courseNotFound);
      return;
    }
    try {
      await apiPost('/matchplays', {
        opponentId: resolvedOpponentId,
        courseId,
        teeId,
        holesToPlay,
      }, token);
      setPendingOpponentId(undefined);
      await loadBootstrapData(token);
      navigateTo('scoreInput');
    } catch (error: any) {
      notify(error?.message ?? t.couldNotStartMatch);
    }
  }

  async function openUserProfile(userId: string) {
    try {
      setSelectedProfile(await apiGet(`/users/${userId}/profile`, token));
      navigateTo('userProfile');
    } catch {
      notify(t.couldNotLoadProfile);
    }
  }

  async function signIn(email: string, password: string) {
    const auth = await authenticateUser('/auth/login', {
      email: email.trim().toLowerCase(),
      password,
    });
    if (auth?.error) {
      return auth.error;
    }
    if (!auth?.token) {
      return t.invalidEmailOrPassword;
    }
    await loadBootstrapData(auth?.token);
    setHistory([]);
    setTab('home');
    navigateTo('main');
  }

  async function registerUser(values: {
    name: string;
    email: string;
    password: string;
    city: string;
    country: string;
    homeClub: string;
    preferredLanguage: Language;
  }) {
    const auth = await authenticateUser('/auth/register', {
      name: values.name,
      email: values.email,
      password: values.password,
      city: values.city,
      country: values.country,
      homeClub: values.homeClub,
      preferredLanguage: values.preferredLanguage,
      systemLanguage: detectSystemLanguageTag(),
    });
    if (auth?.error) {
      throw new Error(auth.error);
    }
    if (!auth?.token) {
      throw new Error(t.couldNotRegisterUser);
    }
    setLanguage(values.preferredLanguage);
    saveGuestLanguagePreference(values.preferredLanguage);
    await loadBootstrapData(auth.token);
    setHistory([]);
    setTab('home');
    navigateTo('main');
  }

  function signOut() {
    setToken(undefined);
    setStoredToken(undefined);
    setCompletedMatch(null);
    setHistoryMatch(null);
    setSelectedProfile(undefined);
    setHistory([]);
    setTab('home');
    setScreen(loggedOutEntryScreen());
  }

  function navigateTo(nextScreen: Screen) {
    if (!authScreens.includes(nextScreen) && redirectIfLoggedOut()) {
      return;
    }
    if (nextScreen !== screen) {
      setHistory(previous => [...previous, screen]);
    }
    setScreen(nextScreen);
  }

  function openPlayDestination() {
    const targetScreen: Screen = data.activeMatch ? 'scoreInput' : 'findMatch';
    setTab('play');
    if (screen !== targetScreen) {
      setHistory([]);
      setScreen(targetScreen);
    }
    if (token) {
      void loadBootstrapData(token);
    }
  }

  function handleBottomTabPress(nextTab: Tab) {
    if (redirectIfLoggedOut()) {
      return;
    }
    if (nextTab === 'play') {
      openPlayDestination();
      return;
    }
    setTab(nextTab);
    navigateTo('main');
  }

  const notificationCount = getNotificationCount(data, notificationCopy(t));

  const content = (() => {
    if (screen === 'landing') {
      return <LandingScreen t={t} setScreen={navigateTo} />;
    }
    if (screen === 'welcome') {
      return <WelcomeScreen t={t} setScreen={navigateTo} onSignIn={signIn} />;
    }
    if (screen === 'createAccount') {
      return (
        <CreateAccountScreen
          t={t}
          language={language}
          setLanguage={setAppLanguage}
          onRegister={registerUser}
          setScreen={navigateTo}
        />
      );
    }
    if (screen === 'legalTerms') {
      return <LegalPageScreen t={t} docKey="terms" onBack={() => setScreen('landing')} />;
    }
    if (screen === 'legalPrivacy') {
      return <LegalPageScreen t={t} docKey="privacy" onBack={() => setScreen('landing')} />;
    }
    if (screen === 'legalCookies') {
      return <LegalPageScreen t={t} docKey="cookies" onBack={() => setScreen('landing')} />;
    }
    if (!token) {
      return <LandingScreen t={t} setScreen={navigateTo} />;
    }
    if (screen === 'findMatch') {
      return (
        <FindMatchScreen
          t={t}
          data={data}
          setScreen={openScreen}
          notificationCount={notificationCount}
          action={actionAndRefresh}
          openUserProfile={openUserProfile}
          onStartMatch={openCourseSelect}
          onOpenActiveMatch={openActiveMatch}
          onCancelMatch={cancelActiveMatch}
          notify={notify}
        />
      );
    }
    if (screen === 'selectCourse') {
      return (
        <CourseSelectScreen
          t={t}
          data={data}
          setScreen={openScreen}
          notificationCount={notificationCount}
          opponentId={pendingOpponentId}
          onStart={(courseId, teeId, holesToPlay) => startMatchplay(courseId, teeId, holesToPlay, pendingOpponentId)}
        />
      );
    }
    if (screen === 'scoreInput') {
      return (
        <ScoreInputScreen
          t={t}
          data={data}
          token={token}
          setScreen={openScreen}
          notificationCount={notificationCount}
          onRefresh={refreshBootstrap}
          onCancelMatch={cancelActiveMatch}
          onMatchCancelled={handleMatchCancelled}
          onComplete={(match) => {
            setCompletedMatch(match);
            navigateTo('matchResult');
          }}
        />
      );
    }
    if (screen === 'matchResult') {
      return (
        <MatchResultScreen
          t={t}
          match={completedMatch}
          token={token}
          setScreen={openScreen}
          notificationCount={notificationCount}
          onOpenHistory={showMatchDetail}
          onStatsSaved={() => loadBootstrapData(token)}
        />
      );
    }
    if (screen === 'matchHistory') {
      return <MatchHistoryScreen t={t} match={historyMatch} setScreen={openScreen} notificationCount={notificationCount} onBack={goBack} />;
    }
    if (screen === 'golfClub') {
      return <GolfClubScreen t={t} data={data} setScreen={openScreen} notificationCount={notificationCount} action={action} />;
    }
    if (screen === 'academy') {
      return <AcademyScreen t={t} data={data} setScreen={openScreen} notificationCount={notificationCount} action={action} />;
    }
    if (screen === 'partners') {
      return <PartnersScreen t={t} data={data} setScreen={openScreen} notificationCount={notificationCount} action={action} />;
    }
    if (screen === 'tournament') {
      return <TournamentScreen t={t} data={data} action={action} setScreen={openScreen} notificationCount={notificationCount} />;
    }
    if (screen === 'matchplaySetup') {
      return (
        <MatchplaySetupScreen
          t={t}
          data={data}
          setScreen={openScreen}
          notificationCount={notificationCount}
          onChooseCourse={() => openCourseSelect(data.friends?.[0]?.id)}
        />
      );
    }
    if (screen === 'friends') {
      return (
        <FriendsScreen
          t={t}
          data={data}
          setScreen={openScreen}
          notificationCount={notificationCount}
          notify={notify}
          action={actionAndRefresh}
          openUserProfile={openUserProfile}
          onStartMatch={openCourseSelect}
          onOpenActiveMatch={openActiveMatch}
          onCancelMatch={cancelActiveMatch}
        />
      );
    }
    if (screen === 'communities') {
      return <CommunitiesScreen t={t} data={data} setScreen={openScreen} notificationCount={notificationCount} action={actionAndRefresh} />;
    }
    if (screen === 'events') {
      return <EventsScreen t={t} data={data} setScreen={openScreen} notificationCount={notificationCount} action={actionAndRefresh} />;
    }
    if (screen === 'notifications') {
      return (
        <NotificationsScreen
          t={t}
          data={data}
          token={token}
          notificationCount={notificationCount}
          action={actionAndRefresh}
          setScreen={openScreen}
          onOpenActiveMatch={openActiveMatch}
          onOpenMatchHistory={showMatchResult}
          onMatchCancelled={handleMatchCancelled}
          onNotificationTap={handleNotificationTap}
          onNotificationDismiss={handleNotificationDismiss}
          onReadAll={handleReadAllNotifications}
        />
      );
    }
    if (screen === 'userProfile') {
      return <UserProfileScreen t={t} profile={selectedProfile} loading={profileRouteLoading} setScreen={openScreen} notificationCount={notificationCount} />;
    }
    if (tab === 'rank') {
      return <LeaderboardScreen t={t} data={data} token={token} setScreen={openScreen} action={action} notificationCount={notificationCount} openUserProfile={openUserProfile} />;
    }
    if (tab === 'profile') {
      return <ProfileScreen t={t} data={data} token={token} setScreen={openScreen} notificationCount={notificationCount} onOpenMatch={showMatchDetail} onAvatarUpdated={() => loadBootstrapData(token)} />;
    }
    if (tab === 'settings') {
      return <SettingsScreen t={t} data={data} language={language} setLanguage={setAppLanguage} setScreen={openScreen} notificationCount={notificationCount} onSignOut={signOut} />;
    }
    return <HomeScreen t={t} data={data} setScreen={openScreen} setTab={setTab} action={actionAndRefresh} onOpenActiveMatch={openActiveMatch} onOpenMatchHistory={showMatchResult} onCancelMatch={cancelActiveMatch} onMatchCancelled={handleMatchCancelled} onNotificationTap={handleNotificationTap} onOpenProfile={openUserProfile} notify={notify} />;
  })();

  const showBottomTabs = Boolean(token) && !authScreens.includes(screen);

  if (showSplash) {
    return (
      <SafeAreaProvider>
        <StatusBar barStyle="light-content" />
        <SplashScreen />
      </SafeAreaProvider>
    );
  }

  const mainContent = content;

  return (
    <SafeAreaProvider>
      <StatusBar barStyle="dark-content" />
      <SafeAreaView style={[styles.app, Platform.OS === 'web' && styles.webApp]}>
        {serviceUnavailable ? (
          <AppStatusBanner
            message={t.serviceUnavailable}
            actionLabel={t.retryAction}
            onAction={() => {
              void checkApiHealth().then(ok => {
                if (ok) {
                  void loadBootstrapData(token);
                }
              });
            }}
          />
        ) : appUpdateAvailable ? (
          <AppStatusBanner
            message={t.appUpdateAvailable}
            actionLabel={t.appUpdateAction}
            onAction={() => applyAppUpdate(true)}
          />
        ) : null}
        {screen === 'selectCourse' ? (
          <View style={[styles.appScroll, styles.courseSelectOuter]}>
            {content}
          </View>
        ) : (
          <ScrollView
            ref={scrollRef}
            style={styles.appScroll}
            contentContainerStyle={[
              styles.scroll,
              authScreens.includes(screen) && styles.authScroll,
            ]}
            keyboardShouldPersistTaps="handled"
            refreshControl={authScreens.includes(screen) ? undefined : <RefreshControl refreshing={refreshing} onRefresh={refreshBootstrap} />}
          >
            {mainContent}
          </ScrollView>
        )}
        {showBottomTabs && (
          <BottomTabs t={t} active={tab} onTabPress={handleBottomTabPress} />
        )}
      </SafeAreaView>
    </SafeAreaProvider>
  );
}

function notificationCopy(t: typeof dictionary.en): NotificationCopy {
  return {
    friendRequestFrom: t.friendRequestFrom,
    clubMember: t.clubMember,
    handicapShort: t.handicapShort,
  };
}

function AppScreenTitle({
  title,
  setScreen,
  notificationCount,
  accessory,
  showBell = true,
}: {
  title: string;
  setScreen: (screen: Screen) => void;
  notificationCount: number;
  accessory?: ReactNode;
  showBell?: boolean;
}) {
  return (
    <ScreenTitleRow
      title={title}
      notificationCount={notificationCount}
      accessory={accessory}
      onPressNotifications={showBell ? () => setScreen('notifications') : undefined}
    />
  );
}

function openHomeBanner(
  banner: BootstrapData['banners'][number],
  setScreen: (screen: Screen) => void,
) {
  if (banner.targetType === 'event') {
    setScreen('events');
    return;
  }
  if (banner.targetType === 'lesson') {
    setScreen('academy');
    return;
  }
  if (banner.targetType === 'tournament') {
    setScreen('tournament');
    return;
  }
  if (banner.targetUrl) {
    void Linking.openURL(banner.targetUrl);
  }
}

function HomeScreen({ t, data, setScreen, setTab, action: _action, onOpenActiveMatch, onOpenMatchHistory, onCancelMatch, onMatchCancelled, onNotificationTap, onOpenProfile, notify }: Omit<ScreenProps, 'notificationCount'> & { setTab: (tab: Tab) => void; onOpenActiveMatch: () => void; onOpenMatchHistory: (matchId: string) => void; onCancelMatch: (matchId?: string) => void; onMatchCancelled: () => void; onNotificationTap: (item: NotificationFeedItem, pressHandlers: Parameters<typeof handleNotificationPress>[1]) => Promise<void>; onOpenProfile: (userId: string) => Promise<void>; notify: (message: string) => void }) {
  const copy = notificationCopy(t);
  const notificationCount = getNotificationCount(data, copy);
  const homePills = [
    { id: 'CLUBS' as const, label: t.pillClubs },
    { id: 'COMMUNITIES' as const, label: t.pillCommunities },
    { id: 'RANK' as const, label: t.pillRank },
    { id: 'ACADEMY' as const, label: t.pillAcademy },
    { id: 'TOURNAMENTS' as const, label: t.pillTournaments },
  ];

  const events = data.events ?? [];
  const featuredEvent = events.find(event => event.videoUrl) ?? events.find(event => event.status === 'open') ?? events[0];

  function handleHomePill(label: string) {
    const pill = homePills.find(item => item.label === label);
    switch (pill?.id) {
      case 'CLUBS':
        setScreen('golfClub');
        break;
      case 'COMMUNITIES':
        setScreen('communities');
        break;
      case 'RANK':
        setTab('rank');
        setScreen('main');
        break;
      case 'ACADEMY':
        setScreen('academy');
        break;
      case 'TOURNAMENTS':
        setScreen('tournament');
        break;
    }
  }

  return (
    <View>
      <Header
        title={PRODUCT_NAME}
        notificationCount={notificationCount}
        onPressNotifications={() => setScreen('notifications')}
      />
      <PillRow items={homePills.map(item => item.label)} active="" setActive={handleHomePill} />
      <HomeCardPreview t={t} data={data} notify={notify} />
      {data.activeMatch && (
        <ActiveMatchCard t={t} match={data.activeMatch} onPress={onOpenActiveMatch} onCancel={() => onCancelMatch(data.activeMatch?.id)} />
      )}
      <HomeNotifications
        t={t}
        data={data}
        setScreen={setScreen}
        onOpenActiveMatch={onOpenActiveMatch}
        onOpenMatchHistory={onOpenMatchHistory}
        onMatchCancelled={onMatchCancelled}
        onNotificationTap={onNotificationTap}
      />
      {featuredEvent ? (
        <FeaturedEventCard
          t={t}
          title={featuredEvent.title}
          subtitle={featuredEvent.description}
          cta={t.viewAction}
          videoUrl={featuredEvent.videoUrl}
          imageUrl={featuredEvent.imageUrl}
          onPress={() => setScreen('events')}
        />
      ) : null}
      {data.banners.map(banner => (
        <FeaturedEventCard
          key={banner.id}
          t={t}
          label={t.promo}
          title={banner.title}
          subtitle={banner.subtitle}
          cta={banner.targetType === 'lesson' ? t.academy : t.joinNow}
          imageUrl={banner.imageUrl}
          onPress={() => openHomeBanner(banner, setScreen)}
        />
      ))}
      <SectionHeader title={t.upcoming} action={t.seeAll} onPress={() => setScreen('events')} />
      <View style={styles.horizontalCards}>
        {data.tournaments.map(item => (
          <TouchableOpacity key={item.id} style={styles.eventCard} onPress={() => setScreen('tournament')}>
            <Text style={styles.tag}>{t.tagGolf}</Text>
            <Text style={styles.cardTitle}>{item.title}</Text>
            <Text style={styles.muted}>{item.date} {item.time} / {item.status}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <Text style={styles.sectionTitle}>{t.quickAccess}</Text>
      <View style={styles.grid}>
        <QuickAction label={t.bookTeeTime} detail={t.detailSoon} icon="calendar" disabled onPress={() => undefined} />
        <QuickAction label={t.findMatch} detail={t.detailFriendsMatch} icon="users" onPress={() => setScreen('findMatch')} />
        <QuickAction label={t.myRating} detail={t.detailViewStats} icon="trending-up" onPress={() => { setTab('rank'); setScreen('main'); }} />
        <QuickAction label={t.partners} detail={t.detailCashback} icon="percent" onPress={() => setScreen('partners')} />
        <QuickAction label={t.academy} detail={t.detailBookLesson} icon="graduation-cap" onPress={() => setScreen('academy')} />
        <QuickAction label={t.communities} detail={t.detailJoinSelect} icon="building" onPress={() => setScreen('communities')} />
        <QuickAction label={t.friendsLabel} detail={t.detailAddView} icon="user-plus" onPress={() => setScreen('friends')} />
        <QuickAction label={t.eventsLabel} detail={t.detailSignups} icon="calendar-check" onPress={() => setScreen('events')} />
      </View>
      <LegalFooter t={t} />
    </View>
  );
}

function ActiveMatchCard({ t, match, onPress, onCancel }: { t: typeof dictionary.en; match: ActiveMatch; onPress: () => void; onCancel: () => void }) {
  const me = match.participants.find(participant => participant.id === match.meParticipantId);
  const opponent = match.participants.find(participant => participant.id !== match.meParticipantId);
  const holesTotal = match.holesToPlay ?? match.course?.holes.length ?? 9;
  const holeNumbers = Array.from({ length: holesTotal }, (_, index) => index + 1);
  const reports = reportsFromLegacyHoleStats(match.scoreReports, match.participants);
  const holesDone = me
    ? holeNumbers.filter(holeNumber => displayedStrokes(reports, me.id, me.id, holeNumber).strokes != null).length
    : 0;
  const progress = holesTotal > 0 ? Math.min(100, Math.round((holesDone / holesTotal) * 100)) : 0;
  const statusLabel = match.bothConfirmed
    ? t.finishingMatch
    : match.readyToConfirm
      ? me?.scoreConfirmedAt ? t.waitingOpponentConfirmation : t.confirmYourScorecard
      : match.coverageComplete
        ? t.reviewScorecardTitle
        : formatMessage(t.enterScoresProgress, { done: holesDone, total: holesTotal });
  const ctaLabel = match.readyToConfirm && !me?.scoreConfirmedAt
    ? t.confirmScorecardArrow
    : match.coverageComplete
      ? t.reviewScorecard
      : t.continueScoringArrow;
  const teeName = match.tee?.name ?? t.defaultTeeName;

  return (
    <View>
      <TouchableOpacity style={styles.activeMatchCard} onPress={onPress} activeOpacity={0.92}>
        <View style={styles.activeMatchGlow} />
        <View style={styles.activeMatchHeader}>
          <Text style={styles.activeMatchBadge}>{t.liveMatch}</Text>
          <Text style={styles.activeMatchBadge}>{teeName} {t.teesWord}</Text>
        </View>
        <Text style={styles.activeMatchTitle}>{match.course?.name ?? t.matchplayDefault}</Text>
        <Text style={styles.activeMatchSubtitle}>vs {match.opponentName} · {statusLabel}</Text>
        <View style={styles.activeMatchPlayers}>
          <View style={styles.activeMatchPlayerBlock}>
            <Avatar initials={me?.initials ?? 'ME'} photoUrl={me?.photoUrl} activeMatch muted={holesDone === 0} />
            <Text style={styles.activeMatchPlayerName}>{me?.name ?? t.you}</Text>
            <Text style={styles.activeMatchPlayerStatus}>{me?.scoreConfirmedAt ? t.submitted : t.inProgress}</Text>
          </View>
          <Text style={styles.activeMatchVs}>{t.vsLabel}</Text>
          <View style={styles.activeMatchPlayerBlock}>
            <Avatar initials={opponent?.initials ?? 'OP'} photoUrl={opponent?.photoUrl} activeMatch muted={!match.coverageComplete} />
            <Text style={styles.activeMatchPlayerName}>{opponent?.name ?? t.opponent}</Text>
            <Text style={styles.activeMatchPlayerStatus}>{opponent?.scoreConfirmedAt ? t.submitted : t.inProgress}</Text>
          </View>
        </View>
        {!match.coverageComplete && (
          <>
            <View style={styles.activeMatchProgressTrack}>
              <View style={[styles.activeMatchProgressFill, { width: `${progress}%` }]} />
            </View>
            <Text style={styles.activeMatchProgressLabel}>{formatMessage(t.yourProgress, { done: holesDone, total: holesTotal })}</Text>
          </>
        )}
        <View style={styles.activeMatchCta}>
          <Text style={styles.activeMatchCtaText}>{ctaLabel}</Text>
        </View>
      </TouchableOpacity>
      <TouchableOpacity style={styles.cancelMatchButton} onPress={onCancel}>
        <Text style={styles.cancelMatchLink}>{t.cancelMatch}</Text>
      </TouchableOpacity>
    </View>
  );
}

function HomeNotifications({
  t,
  data,
  setScreen,
  onOpenActiveMatch,
  onOpenMatchHistory,
  onMatchCancelled,
  onNotificationTap,
}: {
  t: typeof dictionary.en;
  data: BootstrapData;
  setScreen: (screen: Screen) => void;
  onOpenActiveMatch: () => void;
  onOpenMatchHistory: (matchId: string) => void;
  onMatchCancelled: () => void;
  onNotificationTap: (item: NotificationFeedItem, pressHandlers: Parameters<typeof handleNotificationPress>[1]) => Promise<void>;
}) {
  const copy = notificationCopy(t);
  const feed = buildNotificationFeed(data, copy);
  if (feed.length === 0) {
    return null;
  }

  const preview = getHomeNotificationPreview(data, copy);
  const pressHandlers = { onOpenActiveMatch, onOpenMatchHistory, onMatchCancelled, setScreen };

  return (
    <View>
      <SectionHeader
        title={t.notificationsTitle}
        action={feed.length > 3 ? t.seeAll : ''}
        onPress={feed.length > 3 ? () => setScreen('notifications') : undefined}
      />
      {preview.map(item => (
        <NotificationRow
          key={item.id}
          item={item}
          onPress={() => onNotificationTap(item, pressHandlers)}
        />
      ))}
    </View>
  );
}

function NotificationsScreen({
  t,
  data,
  token,
  notificationCount,
  action,
  setScreen,
  onOpenActiveMatch,
  onOpenMatchHistory,
  onMatchCancelled,
  onNotificationTap,
  onNotificationDismiss,
  onReadAll,
}: {
  t: typeof dictionary.en;
  data: BootstrapData;
  token?: string;
  notificationCount: number;
  action: (path: string, payload: object, message: string) => Promise<void>;
  setScreen: (screen: Screen) => void;
  onOpenActiveMatch: () => void;
  onOpenMatchHistory: (matchId: string) => void;
  onMatchCancelled: () => void;
  onNotificationTap: (item: NotificationFeedItem, pressHandlers: Parameters<typeof handleNotificationPress>[1]) => Promise<void>;
  onNotificationDismiss: (item: NotificationFeedItem) => Promise<void>;
  onReadAll: () => Promise<void>;
}) {
  const [history, setHistory] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    if (!token) {
      setHistory([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setHistory(await apiGet('/notifications', token));
    } catch {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, data.notifications.length, data.friendRequests?.length]);

  const copy = notificationCopy(t);
  const feed = buildNotificationHistoryFeed(history, data.friendRequests, copy);
  const unreadCount = getUnreadNotificationCount(feed);
  const pressHandlers = { onOpenActiveMatch, onOpenMatchHistory, onMatchCancelled, setScreen };

  async function dismiss(item: NotificationFeedItem) {
    await onNotificationDismiss(item);
    await loadHistory();
  }

  async function readAll() {
    await onReadAll();
    await loadHistory();
  }

  if (loading && feed.length === 0) {
    return (
      <View>
        <AppScreenTitle title={t.notificationsTitle} setScreen={setScreen} notificationCount={notificationCount} showBell={false} />
        <Text style={styles.muted}>{t.loadingNotifications}</Text>
      </View>
    );
  }

  if (feed.length === 0) {
    return (
      <View>
        <AppScreenTitle title={t.notificationsTitle} setScreen={setScreen} notificationCount={notificationCount} showBell={false} />
        <Text style={styles.muted}>{t.allCaughtUp}</Text>
      </View>
    );
  }

  return (
    <View>
      <AppScreenTitle
        title={t.notificationsTitle}
        setScreen={setScreen}
        notificationCount={notificationCount}
        showBell={false}
        accessory={
          <TouchableOpacity
            style={[styles.notificationReadAllButton, unreadCount === 0 && styles.notificationReadAllButtonDisabled]}
            disabled={unreadCount === 0}
            onPress={readAll}
          >
            <Text style={styles.notificationReadAllText}>{t.readAll}</Text>
          </TouchableOpacity>
        }
      />
      {feed.map(item => (
        <NotificationRow
          key={item.id}
          item={item}
          onPress={async () => {
            await onNotificationTap(item, pressHandlers);
            await loadHistory();
          }}
          onDismiss={() => dismiss(item)}
          footer={
            item.kind === 'friendRequest' && !item.read ? (
              <View style={styles.notificationActions}>
                <TouchableOpacity
                  style={styles.pillButton}
                  onPress={() => action(`/friends/${item.requestId}/accept`, {}, t.friendRequestAccepted)}
                >
                  <Text style={styles.pillButtonText}>{t.accept}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.secondaryMiniButton} onPress={() => setScreen('friends')}>
                  <Text style={styles.secondaryButtonText}>{t.viewAction}</Text>
                </TouchableOpacity>
              </View>
            ) : undefined
          }
        />
      ))}
    </View>
  );
}

function HandicapCardStats({ t, fields }: { t: typeof dictionary.en; fields: HandicapFields }) {
  const index = resolveHandicapIndex(fields);
  return (
    <View style={styles.memberCardStats}>
      <View style={styles.memberCardStat}>
        <Text style={styles.memberCardStatLabel}>{t.handicapIndexShort}</Text>
        <Text style={styles.memberCardStatValue}>{formatHandicapIndex(index)}</Text>
      </View>
      <View style={styles.memberCardStat}>
        <Text style={styles.memberCardStatLabel}>{t.courseHandicapShort}</Text>
        <Text style={styles.memberCardStatValue}>{formatCourseHandicap(fields.courseHandicap)}</Text>
      </View>
      <View style={styles.memberCardMemberPill}>
        <Text style={styles.memberCardMemberText}>{t.memberBadge}</Text>
      </View>
    </View>
  );
}

function HandicapProfilePills({ t, fields }: { t: typeof dictionary.en; fields: HandicapFields }) {
  const index = resolveHandicapIndex(fields);
  const courseLabel = courseHandicapLabel(fields);
  return (
    <View style={styles.handicapProfileBlock}>
      <View style={styles.pillLine}>
        <Text style={styles.lightPill}>{t.handicapIndexShort} {formatHandicapIndex(index)}</Text>
        <Text style={styles.lightPill}>{t.courseHandicapShort} {formatCourseHandicap(fields.courseHandicap)}</Text>
      </View>
      {courseLabel ? <Text style={styles.handicapCourseLabel}>{formatMessage(t.courseHandicapAt, { course: courseLabel })}</Text> : null}
    </View>
  );
}

function PlayerRecordLine({
  t,
  player,
  style,
}: {
  t: typeof dictionary.en;
  player: PlayerRecordFields;
  style?: object;
}) {
  return (
    <Text style={style ?? styles.playerRecord}>
      {formatPlayerRecord(player, {
        handicapShort: t.handicapShort,
        pointsShort: t.pointsShort,
        winsLossesShort: t.winsLossesShort,
        winsLossesDrawsShort: t.winsLossesDrawsShort,
        provisional: t.provisionalBadge,
      })}
    </Text>
  );
}

function HomeCardPreview({
  t,
  data,
  notify,
}: {
  t: typeof dictionary.en;
  data: BootstrapData;
  notify: (message: string) => void;
}) {
  const [showQr, setShowQr] = useState(false);
  const handle = data.player.handle ?? fallbackHandleFromPlayer(data.player);
  const link = data.player.profileUrl ?? (
    Platform.OS === 'web' && typeof window !== 'undefined'
      ? `${window.location.origin}/@${encodeURIComponent(handle)}`
      : profileUrl(handle)
  );

  async function shareProfileLink() {
    try {
      const result = await shareContent({
        title: t.profileShareTitle,
        message: formatMessage(t.profileShareMessage, { handle: profileHandleLabel(handle) }),
        url: link,
      });
      if (result === 'copied') {
        notify(t.profileLinkCopied);
      }
    } catch {
      notify(t.couldNotShareProfile);
    }
  }

  return (
    <View>
      <Text style={styles.sectionTitle}>{t.card}</Text>
      <View style={[styles.profileHeroCard, { marginBottom: 16 }]}>
        {!showQr ? (
          <TouchableOpacity
            style={styles.cardShareButton}
            onPress={() => void shareProfileLink()}
            accessibilityLabel="Share profile"
            accessibilityRole="button"
          >
            <Text style={styles.cardShareIcon}>↗</Text>
          </TouchableOpacity>
        ) : null}
        <TouchableOpacity
          onPress={() => setShowQr(current => !current)}
          activeOpacity={0.9}
          style={showQr ? styles.homeCardQrFace : styles.homeCardPressable}
          accessibilityRole="button"
          accessibilityLabel={showQr ? 'Hide profile QR code' : 'Show profile QR code'}
        >
          {showQr ? (
            <ProfileQrCode value={link} />
          ) : (
            <>
              <View style={styles.profileHeroTop}>
                <Avatar large initials={data.player.initials} photoUrl={data.player.photoUrl} />
                <View style={styles.profileHeroCopy}>
                  <Text style={styles.profileHeroName}>{data.player.name}</Text>
                  <Text style={styles.profileHeroClub}>{data.player.club}</Text>
                </View>
              </View>
              <View style={styles.homeCardFooter}>
                <View style={styles.homeCardPills}>
                  <OutlinePill label={`${t.rankPrefix}${data.player.rank}`} />
                  <OutlinePill label={`${t.ratingLabel} ${data.player.elo}`} />
                  <OutlinePill label={`${t.hcpShort} ${formatHandicapIndex(resolveHandicapIndex(data.player))}`} />
                  {data.player.provisional ? <OutlinePill label={t.provisionalBadge} /> : null}
                  <PremiumPill label={t.premium} />
                </View>
              </View>
            </>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

type ScreenProps = {
  t: typeof dictionary.en;
  data: BootstrapData;
  setScreen: (screen: Screen) => void;
  notificationCount: number;
  action: (path: string, payload: object, message: string) => Promise<void>;
};

function LeaderboardScreen({
  t,
  data,
  token,
  action,
  setScreen,
  notificationCount,
  openUserProfile,
}: ScreenProps & { token?: string; openUserProfile: (userId: string) => void }) {
  const filters = [
    { id: 'all', label: t.filterAll },
    { id: 'city', label: t.filterCity },
    { id: 'community', label: t.filterCommunity },
    { id: 'friends', label: t.filterFriends },
  ] as const;
  const [filter, setFilter] = useState<(typeof filters)[number]['id']>('all');
  const [rows, setRows] = useState<Player[]>(data.leaderboard);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) {
        setRows(data.leaderboard);
        return;
      }
      try {
        const next = await apiGet(`/leaderboards/${filter}`, token);
        if (!cancelled && Array.isArray(next)) {
          setRows(next);
        }
      } catch {
        if (!cancelled && filter === 'all') {
          setRows(data.leaderboard);
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [filter, token]);

  const activeLabel = filters.find(item => item.id === filter)?.label ?? t.filterAll;

  return (
    <View>
      <ScreenTitleRow
        title={t.rank}
        notificationCount={notificationCount}
        onPressNotifications={() => setScreen('notifications')}
      />
      <Text style={styles.step}>{t.season}</Text>
      <PillRow
        items={filters.map(item => item.label)}
        active={activeLabel}
        setActive={label => {
          const next = filters.find(item => item.label === label);
          if (next) {
            setFilter(next.id);
          }
        }}
      />
      {rows.length === 0 ? <Text style={styles.muted}>{t.leaderboardEmpty}</Text> : null}
      {rows.length > 0 ? (
        <View style={styles.podium}>
          {rows.slice(0, 3).map(player => (
            <TouchableOpacity
              key={player.id}
              style={styles.podiumPlayer}
              onPress={() => openUserProfile(player.id)}
              accessibilityRole="button"
              accessibilityLabel={player.name}
            >
              <View style={[styles.podiumAvatarWrap, player.rank === 1 && styles.podiumWinnerRing]}>
                <Avatar initials={player.initials} photoUrl={player.photoUrl} />
              </View>
              <Text style={styles.podiumName} numberOfLines={2}>
                {player.name}
              </Text>
              <PlayerRecordLine t={t} player={player} style={styles.podiumRecord} />
            </TouchableOpacity>
          ))}
        </View>
      ) : null}
      {rows.map(player => (
        <View key={player.id} style={[styles.rowCard, player.id === data.player.id && styles.activeRow]}>
          <Text style={styles.rankNumber}>{player.rank}</Text>
          <TouchableOpacity
            style={styles.leaderboardPlayer}
            onPress={() => openUserProfile(player.id)}
            accessibilityRole="button"
            accessibilityLabel={player.name}
          >
            <Avatar initials={player.initials} photoUrl={player.photoUrl} small />
            <View style={styles.rowContent}>
              <Text style={styles.cardTitle}>{player.id === data.player.id ? t.you : player.name}</Text>
              <PlayerRecordLine t={t} player={player} />
            </View>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => action('/duels', { opponentId: player.id }, `${t.challenge}: ${player.name}`)}>
            <Text style={styles.smallButton}>{t.challenge}</Text>
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );
}

function ActiveMatchBanner({
  t,
  match,
  onOpen,
  onCancel,
}: {
  t: typeof dictionary.en;
  match: ActiveMatch;
  onOpen: () => void;
  onCancel: () => void;
}) {
  return (
    <View style={styles.moduleCard}>
      <TouchableOpacity onPress={onOpen}>
        <Text style={styles.cardTitle}>{formatMessage(t.activeMatchVs, { name: match.opponentName })}</Text>
        <Text style={styles.muted}>{t.tapContinueScoring}</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.cancelMatchButton} onPress={onCancel}>
        <Text style={styles.cancelMatchLink}>{t.cancelMatch}</Text>
      </TouchableOpacity>
    </View>
  );
}

function courseHoleCount(course: Course) {
  return course.holesCount ?? course.holes.length;
}

function CourseOption({
  t,
  course,
  selected,
  recent,
  onSelect,
}: {
  t: typeof dictionary.en;
  course: Course;
  selected: boolean;
  recent?: boolean;
  onSelect: () => void;
}) {
  const holes = courseHoleCount(course);
  return (
    <TouchableOpacity
      style={[styles.courseOption, selected && styles.courseOptionActive]}
      onPress={onSelect}
      activeOpacity={0.9}
    >
      <CoverPhoto uri={course.imageUrl} name={course.name} variant="course" />
      <View style={styles.courseOptionHeader}>
        <Text style={styles.courseOptionTitle}>{course.name}</Text>
        <Text style={[styles.courseHoleBadge, holes >= 18 ? styles.courseHoleBadgeFull : styles.courseHoleBadgeShort]}>
          {formatMessage(t.holesCount, { count: holes })}
        </Text>
      </View>
      <Text style={styles.courseOptionMeta}>
        {course.clubName ? `${course.clubName} · ` : ''}{course.city} · Par {course.par}
      </Text>
      <Text style={styles.courseOptionHint}>
        {formatMessage(t.youWillScoreHoles, { holes })}
      </Text>
      <View style={styles.courseOptionBadges}>
        <Text style={styles.courseOptionBadge}>{holes >= 18 ? t.fullRound : t.shortRound}</Text>
        {recent && <Text style={styles.courseOptionBadge}>{t.recentBadge}</Text>}
      </View>
    </TouchableOpacity>
  );
}

function CourseStartPanel({
  t,
  selectedCourse,
  selectedTeeId,
  selectedHolesToPlay,
  effectiveHoleCount,
  isNineHoleCourse,
  canStart,
  onSelectTee,
  onSelectHolesToPlay,
  onStart,
}: {
  t: typeof dictionary.en;
  selectedCourse: Course;
  selectedTeeId?: string;
  selectedHolesToPlay: 9 | 18;
  effectiveHoleCount: number;
  isNineHoleCourse: boolean;
  canStart: boolean;
  onSelectTee: (teeId: string) => void;
  onSelectHolesToPlay: (holes: 9 | 18) => void;
  onStart: () => void;
}) {
  const nineHolesLabel = t.nineHolesOption;
  const eighteenHolesLabel = t.eighteenHolesOption;

  return (
    <View style={styles.courseStartPanel}>
      <View style={styles.selectedCourseHeader}>
        <View style={styles.rowContent}>
          <Text style={styles.courseStartPanelTitle}>{t.selectedCourse}</Text>
          <Text style={styles.muted}>{selectedCourse.name} · Par {selectedCourse.par}</Text>
        </View>
        <Text style={styles.selectedHoleBadge}>{formatMessage(t.holesCount, { count: effectiveHoleCount })}</Text>
      </View>
      <Text style={styles.courseStartHint}>
        {isNineHoleCourse
          ? selectedHolesToPlay === 18
            ? formatMessage(t.selectedCourseHint18, { course: selectedCourse.name })
            : formatMessage(t.selectedCourseHint9, { course: selectedCourse.name })
          : formatMessage(t.matchUsesScorecard, { holes: effectiveHoleCount })}
      </Text>
      {isNineHoleCourse && (
        <>
          <Text style={styles.cardTitle}>{t.roundLength}</Text>
          <PillRow
            items={[nineHolesLabel, eighteenHolesLabel]}
            active={selectedHolesToPlay === 9 ? nineHolesLabel : eighteenHolesLabel}
            setActive={item => onSelectHolesToPlay(item === eighteenHolesLabel ? 18 : 9)}
          />
        </>
      )}
      <Text style={styles.cardTitle}>{t.tees}</Text>
      <View style={styles.teeRow}>
        {selectedCourse.tees.map(tee => (
          <TouchableOpacity key={tee.id} onPress={() => onSelectTee(tee.id)}>
            <Text style={[styles.teeChip, selectedTeeId === tee.id && styles.teeChipActive]}>{tee.name}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <TouchableOpacity
        style={[styles.primaryButton, { marginTop: 16 }, !canStart && styles.disabledQuickCard]}
        disabled={!canStart}
        onPress={onStart}
      >
        <Text style={styles.primaryButtonText}>
          {formatMessage(t.startHoleMatch, { holes: effectiveHoleCount })}
        </Text>
      </TouchableOpacity>
    </View>
  );
}

function CourseSelectScreen({
  t,
  data,
  setScreen,
  notificationCount,
  opponentId,
  onStart,
}: {
  t: typeof dictionary.en;
  data: BootstrapData;
  setScreen: (screen: Screen) => void;
  notificationCount: number;
  opponentId?: string;
  onStart: (courseId: string, teeId: string, holesToPlay: 9 | 18) => void;
}) {
  const [query, setQuery] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState<string>();
  const [selectedTeeId, setSelectedTeeId] = useState<string>();
  const [selectedHolesToPlay, setSelectedHolesToPlay] = useState<9 | 18>(9);

  const courses = data.courses ?? [];
  const recentIds = new Set(data.recentCourseIds ?? []);
  const normalizedQuery = query.trim().toLowerCase();
  const matchesQuery = (course: Course) =>
    !normalizedQuery ||
    course.name.toLowerCase().includes(normalizedQuery) ||
    course.city.toLowerCase().includes(normalizedQuery) ||
    (course.clubName ?? '').toLowerCase().includes(normalizedQuery);

  const recentCourses = courses
    .filter(course => recentIds.has(course.id) && matchesQuery(course))
    .sort((left, right) => (data.recentCourseIds?.indexOf(left.id) ?? 99) - (data.recentCourseIds?.indexOf(right.id) ?? 99));
  const otherCourses = courses.filter(course => !recentIds.has(course.id) && matchesQuery(course));
  const coursesByCity = otherCourses.reduce<Record<string, Course[]>>((groups, course) => {
    const city = course.city || t.countryUae;
    groups[city] = groups[city] ? [...groups[city], course] : [course];
    return groups;
  }, {});
  const cityNames = Object.keys(coursesByCity).sort();

  const opponent =
    opponentId
      ? data.friends?.find(friend => friend.id === opponentId) ?? data.appUsers?.find(user => user.id === opponentId)
      : data.friends?.[0];
  const selectedCourse = courses.find(course => course.id === selectedCourseId);
  const selectedCourseHoleCount = selectedCourse ? courseHoleCount(selectedCourse) : null;
  const isNineHoleCourse = selectedCourseHoleCount === 9;
  const effectiveHoleCount = isNineHoleCourse ? selectedHolesToPlay : (selectedCourseHoleCount ?? 18);
  const canStart = Boolean(selectedCourseId && selectedTeeId && opponent);

  function selectCourse(courseId: string) {
    const course = courses.find(item => item.id === courseId);
    const holes = course ? courseHoleCount(course) : 9;
    setSelectedCourseId(courseId);
    setSelectedTeeId(course?.tees[0]?.id);
    setSelectedHolesToPlay(holes >= 18 ? 18 : 9);
  }

  return (
    <View style={styles.courseSelectScreen}>
      <AppScreenTitle title={t.chooseCourse} setScreen={setScreen} notificationCount={notificationCount} />
      <Text style={styles.courseSelectSubtitle}>
        {opponent ? formatMessage(t.matchVsOpponent, { name: opponent.name }) : t.pickCourseSubtitle}
      </Text>
      <TextInput
        placeholder={t.searchCoursePlaceholder}
        value={query}
        onChangeText={setQuery}
        style={styles.courseSelectSearch}
        placeholderTextColor={colors.muted}
      />
      {selectedCourse && (
        <CourseStartPanel
          t={t}
          selectedCourse={selectedCourse}
          selectedTeeId={selectedTeeId}
          selectedHolesToPlay={selectedHolesToPlay}
          effectiveHoleCount={effectiveHoleCount}
          isNineHoleCourse={isNineHoleCourse}
          canStart={canStart}
          onSelectTee={setSelectedTeeId}
          onSelectHolesToPlay={setSelectedHolesToPlay}
          onStart={() => selectedCourseId && selectedTeeId && onStart(selectedCourseId, selectedTeeId, effectiveHoleCount as 9 | 18)}
        />
      )}
      <View style={styles.courseListViewport}>
        <ScrollView
          style={styles.courseListScroll}
          contentContainerStyle={styles.courseListContent}
          nestedScrollEnabled
          keyboardShouldPersistTaps="handled"
        >
          {!selectedCourse && (
            <View style={styles.courseHelpCard}>
              <Text style={styles.courseHelpTitle}>{t.nineHoleHelpTitle}</Text>
              <Text style={styles.courseHelpText}>{t.nineHoleHelpText}</Text>
            </View>
          )}
          {recentCourses.length > 0 && (
            <>
              <Text style={styles.sectionTitle}>{t.recentCourses}</Text>
              {recentCourses.map(course => (
                <CourseOption
                  t={t}
                  key={course.id}
                  course={course}
                  selected={selectedCourseId === course.id}
                  recent
                  onSelect={() => selectCourse(course.id)}
                />
              ))}
            </>
          )}
          {cityNames.map(city => (
            <View key={city}>
              <Text style={styles.sectionTitle}>{city}</Text>
              {coursesByCity[city].map(course => (
                <CourseOption
                  t={t}
                  key={course.id}
                  course={course}
                  selected={selectedCourseId === course.id}
                  onSelect={() => selectCourse(course.id)}
                />
              ))}
            </View>
          ))}
          {recentCourses.length === 0 && cityNames.length === 0 && (
            <Text style={styles.muted}>{t.noCoursesMatch}</Text>
          )}
        </ScrollView>
      </View>
    </View>
  );
}

function FindMatchScreen({
  t,
  data,
  setScreen,
  notificationCount,
  action,
  openUserProfile,
  onStartMatch,
  onOpenActiveMatch,
  onCancelMatch,
  notify,
}: {
  t: typeof dictionary.en;
  data: BootstrapData;
  setScreen: (screen: Screen) => void;
  notificationCount: number;
  action: (path: string, payload: object, message: string) => Promise<void>;
  openUserProfile: (userId: string) => void;
  onStartMatch: (opponentId?: string) => void;
  onOpenActiveMatch: () => void;
  onCancelMatch: (matchId?: string) => void;
  notify: (message: string) => void;
}) {
  const [query, setQuery] = useState('');
  const directory = filterFriendDirectory(data, query);
  const searchName = query.trim();
  const noSearchResults = Boolean(searchName) && directory.totalCount === 0;

  function handleInviteShared(result: 'shared' | 'copied') {
    if (result === 'copied') {
      notify(t.inviteLinkCopied);
    }
  }

  return (
    <View>
      <ScreenTitleRow
        title={t.findMatch}
        notificationCount={notificationCount}
        onPressNotifications={() => setScreen('notifications')}
      />
      <Text style={styles.subtitle}>{t.findMatchCopy}</Text>
      {data.activeMatch && (
        <ActiveMatchBanner
          t={t}
          match={data.activeMatch}
          onOpen={onOpenActiveMatch}
          onCancel={() => onCancelMatch(data.activeMatch?.id)}
        />
      )}
      <TextInput
        placeholder={t.searchPlayer}
        value={query}
        onChangeText={setQuery}
        style={styles.searchInput}
        placeholderTextColor={colors.muted}
      />
      <InviteFriendCard
        player={data.player}
        title={noSearchResults ? t.inviteFriendNotFoundTitle : t.inviteFriendTitle}
        copy={
          noSearchResults
            ? t.inviteFriendNotFoundCopy.replace('{name}', searchName)
            : t.inviteFriendCopy
        }
        shareLabels={{
          inviteShareTitle: t.inviteShareTitle,
          inviteShareMessage: t.inviteShareMessage,
          inviteShareMessageNamed: t.inviteShareMessageNamed,
        }}
        buttonLabel={t.inviteFriendButton}
        searchName={noSearchResults ? searchName : undefined}
        onShared={handleInviteShared}
        onError={() => notify(t.inviteShareFailed)}
      />
      <FriendsMatchLists
        t={t}
        data={data}
        action={action}
        openUserProfile={openUserProfile}
        onStartMatch={onStartMatch}
        query={query}
        matchMode
      />
    </View>
  );
}

function FriendsMatchLists({
  t,
  data,
  action,
  openUserProfile,
  onStartMatch,
  query = '',
  matchMode = false,
  showRemove = true,
}: {
  t: typeof dictionary.en;
  data: BootstrapData;
  action: (path: string, payload: object, message: string) => Promise<void>;
  openUserProfile: (userId: string) => void;
  onStartMatch: (opponentId?: string) => void;
  query?: string;
  matchMode?: boolean;
  showRemove?: boolean;
}) {
  const { friends, requests, sentRequests, users } = filterFriendDirectory(data, query);

  return (
    <View>
      {requests.length > 0 && <Text style={styles.sectionTitle}>{t.friendRequests}</Text>}
      {requests.map(request => (
        <View key={request.id} style={styles.rowCard}>
          <Avatar initials={request.user.initials} photoUrl={request.user.photoUrl} small />
          <TouchableOpacity style={styles.rowContent} onPress={() => openUserProfile(request.user.id)}>
            <Text style={styles.cardTitle}>{request.user.name}</Text>
            {request.user.homeClub ? <Text style={styles.muted}>{request.user.homeClub}</Text> : null}
            <PlayerRecordLine t={t} player={request.user} />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => action(`/friends/${request.id}/accept`, {}, t.friendRequestAccepted)}>
            <Text style={styles.smallButton}>{t.accept}</Text>
          </TouchableOpacity>
        </View>
      ))}
      {sentRequests.length > 0 && <Text style={styles.sectionTitle}>{t.friendPending}</Text>}
      {sentRequests.map(request => (
        <View key={request.id} style={styles.rowCard}>
          <Avatar initials={request.user.initials} photoUrl={request.user.photoUrl} small />
          <TouchableOpacity style={styles.rowContent} onPress={() => openUserProfile(request.user.id)}>
            <Text style={styles.cardTitle}>{request.user.name}</Text>
            {request.user.homeClub ? <Text style={styles.muted}>{request.user.homeClub}</Text> : null}
            <PlayerRecordLine t={t} player={request.user} />
          </TouchableOpacity>
          <Text style={styles.lightPill}>{t.friendPending}</Text>
        </View>
      ))}
      <Text style={styles.sectionTitle}>{matchMode ? t.friendsStartMatchSection : t.myFriends}</Text>
      {friends.length === 0 && <Text style={styles.muted}>{matchMode ? t.addFriendToMatch : t.noFriendsYet}</Text>}
      {friends.map(friend => (
        <View key={friend.id} style={styles.rowCard}>
          <Avatar initials={friend.initials} photoUrl={friend.photoUrl} small />
          <TouchableOpacity style={styles.rowContent} onPress={() => openUserProfile(friend.id)}>
            <Text style={styles.cardTitle}>{friend.name}</Text>
            {friend.homeClub ? <Text style={styles.muted}>{friend.homeClub}</Text> : null}
            <PlayerRecordLine t={t} player={friend} />
          </TouchableOpacity>
          {showRemove && (
            <TouchableOpacity onPress={() => action(`/friends/${friend.id}/remove`, {}, t.friendRemoved)}>
              <Text style={styles.smallButton}>{t.remove}</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity style={styles.pillButton} onPress={() => onStartMatch(friend.id)}>
            <Text style={styles.pillButtonText}>{t.matchAction}</Text>
          </TouchableOpacity>
        </View>
      ))}
      <Text style={styles.sectionTitle}>{matchMode ? t.allUsersAddFriendSection : t.allUsersSection}</Text>
      {users.map(user => (
        <View key={user.id} style={styles.rowCard}>
          <Avatar initials={user.initials} photoUrl={user.photoUrl} small />
          <TouchableOpacity style={styles.rowContent} onPress={() => openUserProfile(user.id)}>
            <Text style={styles.cardTitle}>{user.name}</Text>
            {user.homeClub ? <Text style={styles.muted}>{user.homeClub}</Text> : null}
            <PlayerRecordLine t={t} player={user} />
          </TouchableOpacity>
          {user.id === data.player.id ? (
            <Text style={styles.lightPill}>{t.meLabel}</Text>
          ) : (
            <TouchableOpacity onPress={() => action('/friends', { userId: user.id }, t.friendRequestSent)}>
              <Text style={styles.smallButton}>{t.addAction}</Text>
            </TouchableOpacity>
          )}
        </View>
      ))}
    </View>
  );
}

function FriendsScreen({
  t,
  data,
  setScreen,
  notificationCount,
  action,
  openUserProfile,
  onStartMatch,
  onOpenActiveMatch,
  onCancelMatch,
  notify,
}: {
  t: typeof dictionary.en;
  data: BootstrapData;
  setScreen: (screen: Screen) => void;
  notificationCount: number;
  action: (path: string, payload: object, message: string) => Promise<void>;
  openUserProfile: (userId: string) => void;
  onStartMatch: (opponentId?: string) => void;
  onOpenActiveMatch: () => void;
  onCancelMatch: (matchId?: string) => void;
  notify: (message: string) => void;
}) {
  function handleInviteShared(result: 'shared' | 'copied') {
    if (result === 'copied') {
      notify(t.inviteLinkCopied);
    }
  }

  return (
    <View>
      <AppScreenTitle title={t.friendsTitle} setScreen={setScreen} notificationCount={notificationCount} />
      {data.activeMatch && (
        <ActiveMatchBanner
          t={t}
          match={data.activeMatch}
          onOpen={onOpenActiveMatch}
          onCancel={() => onCancelMatch(data.activeMatch?.id)}
        />
      )}
      <InviteFriendCard
        player={data.player}
        title={t.inviteFriendTitle}
        copy={t.inviteFriendCopy}
        shareLabels={{
          inviteShareTitle: t.inviteShareTitle,
          inviteShareMessage: t.inviteShareMessage,
          inviteShareMessageNamed: t.inviteShareMessageNamed,
        }}
        buttonLabel={t.inviteFriendButton}
        onShared={handleInviteShared}
        onError={() => notify(t.inviteShareFailed)}
      />
      <FriendsMatchLists
        t={t}
        data={data}
        action={action}
        openUserProfile={openUserProfile}
        onStartMatch={onStartMatch}
      />
    </View>
  );
}

function ScoreInputScreen({
  t,
  data,
  token,
  setScreen,
  notificationCount,
  onRefresh,
  onComplete,
  onCancelMatch,
  onMatchCancelled,
}: {
  t: typeof dictionary.en;
  data: BootstrapData;
  token?: string;
  setScreen: (screen: Screen) => void;
  notificationCount: number;
  onRefresh: () => Promise<BootstrapData>;
  onComplete: (match: ActiveMatch) => void;
  onCancelMatch: (matchId?: string) => void;
  onMatchCancelled: () => void;
}) {
  const [localMatch, setLocalMatch] = useState<ActiveMatch | null>(data.activeMatch ?? null);
  const match = localMatch ?? data.activeMatch;
  const holes = buildMatchHoles(match?.course, match?.holesToPlay);
  const matchHoleTotal = resolveMatchHoleCount(match ?? {});
  const isDoubleNine = matchHoleTotal === 18 && (match?.course?.holesCount ?? match?.course?.holes.length ?? 9) === 9;
  const me = match?.participants.find(participant => participant.id === match?.meParticipantId);
  const opponent = match?.participants.find(participant => participant.id !== match?.meParticipantId);
  const reports = reportsFromLegacyHoleStats(match?.scoreReports, match?.participants ?? []);
  const [holeIndex, setHoleIndex] = useState(() => (
    me ? firstUnscoredHoleIndex(holes, reports, me.id) : 0
  ));
  const [scoreDrafts, setScoreDrafts] = useState(() => (
    me ? buildHoleScoreDrafts(holes, me.id, opponent?.id, reports) : {}
  ));
  const [reviewOpen, setReviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data.activeMatch) {
      setLocalMatch(data.activeMatch);
    }
  }, [data.activeMatch]);

  useEffect(() => {
    if (!match?.id || !me || !holes.length) {
      return;
    }
    const nextReports = reportsFromLegacyHoleStats(match.scoreReports, match.participants);
    setHoleIndex(firstUnscoredHoleIndex(holes, nextReports, me.id));
    setScoreDrafts(buildHoleScoreDrafts(holes, me.id, opponent?.id, nextReports));
    setReviewOpen(
      Boolean(match.coverageComplete)
      && (Boolean(me.scoreSubmittedAt) || !hasReporterEntries(nextReports, me.id)),
    );
    // Reset local drafts only when a different match is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.id]);

  useEffect(() => {
    if (!match?.id || !me || !holes.length) {
      return;
    }
    const nextReports = reportsFromLegacyHoleStats(match.scoreReports, match.participants);
    setScoreDrafts(current => mergeLiveScoreDrafts(current, holes, me.id, opponent?.id, nextReports));
    if (match.coverageComplete && !hasReporterEntries(nextReports, me.id)) {
      setReviewOpen(true);
    }
    // Live reports update drafts; hole list is derived from the same match.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.scoreReports, match?.coverageComplete, match?.id, me?.id, opponent?.id]);

  useEffect(() => {
    if (!match?.id || !token) return;
    if (match.status === 'completed') {
      onComplete(match);
      return;
    }
    if (match.status === 'cancelled') {
      onMatchCancelled();
      return;
    }

    let stopped = false;
    async function checkMatchState() {
      if (!match?.id || !token) return;
      try {
        const fetched = await apiGet(`/matchplays/${match.id}`, token);
        if (stopped) return;
        if (fetched?.status === 'cancelled') {
          onMatchCancelled();
          return;
        }
        if (fetched) {
          setLocalMatch(fetched);
        }
        if (fetched?.status === 'completed') {
          onComplete(fetched);
        }
      } catch {
        // Ignore transient fetch errors while waiting for match state.
      }
    }

    checkMatchState();
    const interval = setInterval(checkMatchState, 3000);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
    // Polling is keyed to the server identity/status rather than transient render objects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.id, match?.status, token]);

  if (!match || !me) {
    return (
      <View>
        <AppScreenTitle title={t.scoreInput.replace('\n', ' ')} setScreen={setScreen} notificationCount={notificationCount} />
        <Text style={styles.muted}>{t.noActiveMatchStart}</Text>
      </View>
    );
  }

  const participantIds = match.participants.map(participant => participant.id);
  const holeNumbers = holes.map(hole => hole.number);
  const currentHole = holes[holeIndex] ?? holes[0];
  const currentDraft = currentHole ? scoreDrafts[currentHole.number] ?? { me: null, opponent: null } : { me: null, opponent: null };
  const myDisplayed = currentHole
    ? displayedStrokes(reports, me.id, me.id, currentHole.number)
    : { strokes: null, source: null };
  const pendingReports = [...reports];
  if (currentHole && currentDraft.me != null) {
    const existingIndex = pendingReports.findIndex(report => (
      report.reporterParticipantId === me.id
      && report.subjectParticipantId === me.id
      && report.holeNumber === currentHole.number
    ));
    const nextReport = {
      reporterParticipantId: me.id,
      subjectParticipantId: me.id,
      holeNumber: currentHole.number,
      strokes: currentDraft.me,
    };
    if (existingIndex >= 0) {
      pendingReports[existingIndex] = nextReport;
    } else {
      pendingReports.push(nextReport);
    }
  }
  if (currentHole && opponent && currentDraft.opponentDirty && currentDraft.opponent != null) {
    const existingIndex = pendingReports.findIndex(report => (
      report.reporterParticipantId === me.id
      && report.subjectParticipantId === opponent.id
      && report.holeNumber === currentHole.number
    ));
    const nextReport = {
      reporterParticipantId: me.id,
      subjectParticipantId: opponent.id,
      holeNumber: currentHole.number,
      strokes: currentDraft.opponent,
    };
    if (existingIndex >= 0) {
      pendingReports[existingIndex] = nextReport;
    } else {
      pendingReports.push(nextReport);
    }
  }
  const pendingCoverage = coverageComplete(participantIds, holeNumbers, pendingReports);
  const holesDone = holeNumbers.filter(holeNumber => displayedStrokes(reports, me.id, me.id, holeNumber).strokes != null).length;
  const onLastHole = holeIndex === holes.length - 1;
  const confirmOnly = Boolean(match.coverageComplete) && !hasReporterEntries(reports, me.id);
  const canConfirm = Boolean(match.readyToConfirm) && !me.scoreConfirmedAt;
  const discrepancies = match.discrepancies ?? [];
  const showReview = reviewOpen || Boolean(me.scoreConfirmedAt);

  function updateDraft(field: 'me' | 'opponent', value: number | null) {
    if (!currentHole) return;
    setScoreDrafts(current => ({
      ...current,
      [currentHole.number]: {
        ...(current[currentHole.number] ?? { me: null, opponent: null }),
        [field]: value,
        [`${field}Dirty`]: true,
      },
    }));
  }

  async function saveCurrentHole() {
    if (!currentHole || !token || !match || !me) return undefined;
    const entries: { participantId: string; holeNumber: number; strokes: number | null }[] = [];
    if (currentDraft.me != null) {
      entries.push({ participantId: me.id, holeNumber: currentHole.number, strokes: currentDraft.me });
    } else if (currentDraft.meDirty) {
      entries.push({ participantId: me.id, holeNumber: currentHole.number, strokes: null });
    }
    if (opponent && currentDraft.opponentDirty) {
      entries.push({ participantId: opponent.id, holeNumber: currentHole.number, strokes: currentDraft.opponent });
    }
    if (!entries.length) {
      return data;
    }
    setBusy(true);
    try {
      const result = await apiPost(`/matchplays/${match.id}/holes`, { entries }, token);
      if (result?.match) {
        setLocalMatch(result.match);
      }
      setScoreDrafts(current => ({
        ...current,
        [currentHole.number]: {
          me: currentDraft.me,
          opponent: currentDraft.opponent,
        },
      }));
      return onRefresh();
    } catch (error: any) {
      alertMessage(t.appName, error?.message ?? t.couldNotSaveHoleScore);
      return undefined;
    } finally {
      setBusy(false);
    }
  }

  async function goToHole(nextIndex: number) {
    const fromIndex = holeIndex;
    const clamped = Math.max(0, Math.min(holes.length - 1, nextIndex));
    const refreshed = await saveCurrentHole();
    const refreshedMatch = refreshed?.activeMatch ?? data.activeMatch ?? localMatch;
    if (refreshedMatch) {
      setLocalMatch(refreshedMatch);
    }
    if (clamped === fromIndex) {
      return;
    }
    setHoleIndex(clamped);
  }

  async function openReview() {
    if (!match || !me) return;
    const refreshed = await saveCurrentHole();
    const nextMatch = refreshed?.activeMatch ?? localMatch ?? match;
    if (nextMatch) {
      setLocalMatch(nextMatch);
    }
    const scoredMatch = nextMatch ?? match;
    const nextReports = reportsFromLegacyHoleStats(nextMatchReports(scoredMatch, pendingReports), scoredMatch.participants);
    if (!coverageComplete(scoredMatch.participants.map(item => item.id), holeNumbers, nextReports) && !pendingCoverage) {
      alertMessage(t.appName, t.waitingForCoverage);
      return;
    }
    if (!confirmOnly) {
      try {
        const result = await apiPost(`/matchplays/${match.id}/submit`, {}, token);
        if (result) {
          setLocalMatch(result);
        }
      } catch (error: any) {
        alertMessage(t.appName, error?.message ?? t.couldNotSubmitScores);
        return;
      }
    }
    setReviewOpen(true);
  }

  async function confirmScores() {
    if (!match) return;
    setBusy(true);
    try {
      const result = await apiPost(`/matchplays/${match.id}/confirm`, {}, token);
      if (!result) {
        alertMessage(t.appName, t.couldNotConfirmScorecard);
        return;
      }
      const finishedMatch = (result?.match ?? result) as ActiveMatch;
      if (finishedMatch?.status === 'completed') {
        setLocalMatch(finishedMatch);
        onComplete(finishedMatch);
        await onRefresh();
        return;
      }
      if (finishedMatch) {
        setLocalMatch(finishedMatch);
      }
      await onRefresh();
    } catch (error: any) {
      alertMessage(t.appName, error?.message ?? t.couldNotConfirmScorecard);
    } finally {
      setBusy(false);
    }
  }

  async function pickScore(subjectParticipantId: string, holeNumber: number, strokes: number) {
    if (!token || !match) return;
    setBusy(true);
    try {
      const result = await apiPost(`/matchplays/${match.id}/picks`, {
        entries: [{ subjectParticipantId, holeNumber, strokes }],
      }, token);
      if (result) {
        setLocalMatch(result);
      }
      await onRefresh();
    } catch (error: any) {
      alertMessage(t.appName, error?.message ?? t.couldNotSavePick);
    } finally {
      setBusy(false);
    }
  }

  const opponentName = opponent?.name ?? match.opponentName;
  const myTotal = holeNumbers.reduce((sum, holeNumber) => sum + (displayedStrokes(reports, me.id, me.id, holeNumber).strokes ?? 0), 0);
  const opponentTotal = opponent
    ? holeNumbers.reduce((sum, holeNumber) => sum + (displayedStrokes(reports, me.id, opponent.id, holeNumber).strokes ?? 0), 0)
    : null;

  if (me.scoreConfirmedAt) {
    return (
      <View>
        <AppScreenTitle title={t.scoreInput.replace('\n', ' ')} setScreen={setScreen} notificationCount={notificationCount} />
        <Text style={styles.subtitle}>{matchCourseSubtitle(t, match, isDoubleNine)}</Text>
        <View style={styles.moduleCard}>
          <Text style={styles.cardTitle}>{t.scorecardConfirmedTitle}</Text>
          <Text style={styles.muted}>{formatMessage(t.waitingForConfirmAuto, { name: opponentName })}</Text>
        </View>
        <StatGrid stats={[
          { value: String(myTotal), label: t.yourScoreStat },
          { value: opponentTotal == null ? '-' : String(opponentTotal), label: t.opponentScoreStat },
          { value: t.waitingOpponentConfirmation, label: t.statusStat },
        ]} />
        <CancelMatchButton t={t} onPress={() => onCancelMatch(match.id)} />
      </View>
    );
  }

  if (showReview) {
    return (
      <View>
        <AppScreenTitle title={t.scoreInput.replace('\n', ' ')} setScreen={setScreen} notificationCount={notificationCount} />
        <Text style={styles.subtitle}>{matchCourseSubtitle(t, match, isDoubleNine)}</Text>
        <View style={styles.moduleCard}>
          <Text style={styles.cardTitle}>{t.reviewScorecardTitle}</Text>
          <Text style={styles.muted}>
            {confirmOnly
              ? formatMessage(t.confirmOnlyHint, { name: opponentName })
              : discrepancies.length
                ? t.pickOfficialScore
                : t.noDiscrepancies}
          </Text>
        </View>
        <StatGrid stats={[
          { value: String(myTotal), label: t.yourScoreStat },
          { value: opponentTotal == null ? '-' : String(opponentTotal), label: t.opponentScoreStat },
          { value: discrepancies.length ? t.scoreDiscrepancyTitle : t.confirmYourScorecard, label: t.statusStat },
        ]} />
        {discrepancies.map(item => {
          const subject = match.participants.find(participant => participant.id === item.subjectParticipantId);
          const myPick = (match.scorePicks ?? []).find(pick => (
            pick.pickerParticipantId === me.id
            && pick.subjectParticipantId === item.subjectParticipantId
            && pick.holeNumber === item.holeNumber
          ));
          const opponentPick = opponent
            ? (match.scorePicks ?? []).find(pick => (
              pick.pickerParticipantId === opponent.id
              && pick.subjectParticipantId === item.subjectParticipantId
              && pick.holeNumber === item.holeNumber
            ))
            : undefined;
          return (
            <View key={`${item.subjectParticipantId}-${item.holeNumber}`} style={styles.moduleCard}>
              <Text style={styles.cardTitle}>
                {formatMessage(t.pickScoreFor, { name: subject?.name ?? t.opponent, hole: item.holeNumber })}
              </Text>
              <View style={styles.discrepancyChoices}>
                {item.values.map(value => (
                  <TouchableOpacity
                    key={value}
                    style={[styles.discrepancyChoice, myPick?.strokes === value && styles.discrepancyChoiceActive]}
                    disabled={busy}
                    onPress={() => pickScore(item.subjectParticipantId, item.holeNumber, value)}
                  >
                    <Text style={styles.cardTitle}>{value}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <Text style={styles.muted}>
                {myPick
                  ? formatMessage(t.youPicked, { score: myPick.strokes })
                  : t.pickOfficialScore}
              </Text>
              <Text style={styles.muted}>
                {opponentPick
                  ? formatMessage(t.opponentPicked, { name: opponentName, score: opponentPick.strokes })
                  : formatMessage(t.waitingForConfirmAuto, { name: opponentName })}
              </Text>
              {myPick && opponentPick && myPick.strokes !== opponentPick.strokes ? (
                <Text style={styles.muted}>{t.waitingForAgreement}</Text>
              ) : null}
            </View>
          );
        })}
        {canConfirm && (
          <TouchableOpacity style={styles.primaryButton} disabled={busy} onPress={confirmScores}>
            <Text style={styles.primaryButtonText}>{t.confirmScorecard}</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity style={styles.secondaryMiniButton} disabled={busy} onPress={() => setReviewOpen(false)}>
          <Text style={styles.secondaryButtonText}>{t.backToHoles}</Text>
        </TouchableOpacity>
        <CancelMatchButton t={t} onPress={() => onCancelMatch(match.id)} />
      </View>
    );
  }

  return (
    <View>
      <AppScreenTitle title={t.scoreInput.replace('\n', ' ')} setScreen={setScreen} notificationCount={notificationCount} />
      <Text style={styles.subtitle}>{matchCourseSubtitle(t, match, isDoubleNine)}</Text>
      <View style={styles.matchPair}>
        <Text style={styles.cardTitle}>{me.name}</Text>
        <Text style={styles.vs}>{t.vsLabel}</Text>
        <Text style={styles.cardTitle}>{opponent?.name ?? match.opponentName}</Text>
      </View>
      <Text style={styles.step}>{formatMessage(t.holesProgress, { done: holesDone, total: holes.length })}</Text>
      <View style={styles.holes}>
        {holes.map((hole, index) => {
          const myScore = displayedStrokes(reports, me.id, me.id, hole.number).strokes;
          const opponentScore = opponent ? displayedStrokes(reports, me.id, opponent.id, hole.number).strokes : null;
          const scoreLabel = savedHoleScoreLabel(myScore, opponentScore);
          const disputed = discrepancies.some(item => item.holeNumber === hole.number);
          return (
            <TouchableOpacity key={`${hole.number}-${hole.round ?? 1}`} onPress={() => goToHole(index)}>
              <Text style={[
                styles.holeChip,
                myScore != null || opponentScore != null ? styles.completedHole : index !== holeIndex ? styles.futureHole : null,
                disputed && styles.disputedHole,
                index === holeIndex && styles.activeHole,
              ]}>
                H{hole.number}{hole.round === 2 ? '²' : ''}{'\n'}{scoreLabel}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
      <View style={styles.scoreCard}>
        <Text style={styles.step}>{formatMessage(t.holeParLabel, { hole: matchHoleLabel(currentHole).toUpperCase(), par: currentHole.par })}</Text>
        {currentHole.distance ? (
          <Text style={styles.muted}>
            {formatMessage(t.holeDistanceLabel, {
              distance: currentHole.distance,
              unit: match.course?.distanceUnit === 'meters' ? t.metersShort : t.yardsShort,
            })}
          </Text>
        ) : null}
        <View style={styles.scoreLine}>
          <ScoreStepper
            label={me.name}
            value={currentDraft.me}
            setValue={value => updateDraft('me', value)}
            hint={myDisplayed.source === 'theirs' && !currentDraft.meDirty
              ? formatMessage(t.fromOpponent, { name: opponentName })
              : undefined}
          />
          {opponent ? (
            <ScoreStepper
              label={opponent.name}
              value={currentDraft.opponent}
              setValue={value => updateDraft('opponent', value)}
              hint={t.optionalScore}
            />
          ) : null}
        </View>
        <View style={styles.row}>
          <TouchableOpacity style={styles.secondaryMiniButton} disabled={holeIndex === 0 || busy} onPress={() => goToHole(holeIndex - 1)}>
            <Text style={styles.secondaryButtonText}>{t.prevButton}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={pendingCoverage ? [styles.primaryButton, styles.primaryButtonInline] : styles.pillButton}
            disabled={busy}
            onPress={() => (pendingCoverage ? openReview() : goToHole(holeIndex + 1))}
          >
            <Text style={pendingCoverage ? styles.primaryButtonText : styles.pillButtonText}>
              {pendingCoverage ? t.reviewScorecard : onLastHole ? t.saveLastHole : t.nextHole}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
      <CancelMatchButton t={t} onPress={() => onCancelMatch(match.id)} />
    </View>
  );
}

function nextMatchReports(match: ActiveMatch | null | undefined, fallback: ReturnType<typeof reportsFromLegacyHoleStats>) {
  if (!match) return fallback;
  return reportsFromLegacyHoleStats(match.scoreReports, match.participants);
}

function matchCourseSubtitle(t: typeof dictionary.en, match: ActiveMatch, isDoubleNine: boolean) {
  const tee = match.tee?.name ?? t.defaultTeeName;
  return `${match.course?.name} · ${tee} ${t.teesWord}${isDoubleNine ? t.twoRoundsSuffix : ''}`;
}

function CancelMatchButton({ t, onPress }: { t: typeof dictionary.en; onPress: () => void }) {
  return (
    <TouchableOpacity style={styles.cancelMatchButton} onPress={onPress}>
      <Text style={styles.cancelMatchLink}>{t.cancelMatch}</Text>
    </TouchableOpacity>
  );
}

function getPlayerMatchResult(match: ActiveMatch, me?: ActiveMatch['participants'][number]) {
  if (!me) return 'D';
  if (me.result === 'W' || me.result === 'L' || me.result === 'D') return me.result;
  if (!match.winnerId) return 'D';
  return me.userId === match.winnerId ? 'W' : 'L';
}

function normalizeMatchResult(result?: string | null): 'W' | 'L' | 'D' {
  const code = String(result ?? '').trim().toUpperCase();
  if (code === 'W' || code === 'L' || code === 'D') return code;
  return 'D';
}

function matchResultStyles(result?: string | null) {
  const code = normalizeMatchResult(result);
  if (code === 'L') {
    return {
      chip: styles.resultChipLoss,
      chipText: styles.resultChipTextLoss,
      pill: styles.lossPill,
      pillText: styles.lossPillText,
    };
  }
  if (code === 'D') {
    return {
      chip: styles.resultChipDraw,
      chipText: styles.resultChipTextDraw,
      pill: styles.drawPill,
      pillText: styles.drawPillText,
    };
  }
  return {
    chip: styles.resultChipWin,
    chipText: styles.resultChipTextWin,
    pill: styles.winPill,
    pillText: styles.winPillText,
  };
}

function courseStatFontSize(name: string) {
  if (name.length > 28) return 11;
  if (name.length > 20) return 13;
  if (name.length > 14) return 15;
  return 18;
}

function formatMatchDate(iso?: string) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function ScorecardRow({
  label,
  values,
  accent,
  compact,
}: {
  label: string;
  values: (string | number)[];
  accent?: boolean;
  compact?: boolean;
}) {
  return (
    <View style={[styles.scorecardRow, accent && styles.scorecardRowAccent]}>
      <Text style={[styles.scorecardLabel, accent && styles.scorecardLabelAccent]}>{label}</Text>
      <View style={styles.scorecardValues}>
        {values.map((value, index) => (
          <Text
            key={`${label}-${index}`}
            style={[
              styles.scorecardCell,
              accent && styles.scorecardCellAccent,
              compact && styles.scorecardCellCompact,
            ]}
          >
            {value}
          </Text>
        ))}
      </View>
    </View>
  );
}

function CompletedMatchView({
  t,
  match,
  title,
  setScreen,
  notificationCount,
  footer,
}: {
  t: typeof dictionary.en;
  match: ActiveMatch;
  title: string;
  setScreen: (screen: Screen) => void;
  notificationCount: number;
  footer?: ReactNode;
}) {
  const me = match.participants.find(participant => participant.id === match.meParticipantId);
  const opponent = match.participants.find(participant => participant.id !== match.meParticipantId);
  const myScore = me?.grossScore ?? me?.holeStats.reduce((sum, stat) => sum + stat.strokes, 0) ?? 0;
  const opponentScore = opponent?.grossScore ?? opponent?.holeStats.reduce((sum, stat) => sum + stat.strokes, 0) ?? 0;
  const resultCode = getPlayerMatchResult(match, me);
  const didWin = resultCode === 'W';
  const didLose = resultCode === 'L';
  const headerResultStyles = matchResultStyles(resultCode);
  const courseName = match.course?.name ?? t.golfClub;
  const holes = match.course?.holes ?? [];
  const resultLabel = didWin ? t.resultWin : didLose ? t.resultLoss : t.resultDraw;

  return (
    <View>
      <AppScreenTitle
        title={title}
        setScreen={setScreen}
        notificationCount={notificationCount}
        accessory={
          <Text style={[headerResultStyles.pill, headerResultStyles.pillText]}>
            {resultLabel}
          </Text>
        }
      />
      <Text style={styles.subtitle}>
        {me?.name ?? t.you} vs {opponent?.name ?? match.opponentName}
        {match.playedAt ? ` · ${formatMatchDate(match.playedAt)}` : ''}
        {match.tee?.name ? ` · ${match.tee.name} ${t.teesWord}` : ''}
      </Text>
      <View style={styles.resultCard}>
        <View style={styles.resultLine}>
          <Avatar initials={me?.initials ?? 'ME'} photoUrl={me?.photoUrl} />
          <View style={styles.resultScoreBlock}>
            <Text style={styles.resultScore}>{myScore}</Text>
            <Text style={styles.vsLabel}>{t.vsLabel}</Text>
            <Text style={styles.resultScore}>{opponentScore}</Text>
          </View>
          <Avatar initials={opponent?.initials ?? 'OP'} photoUrl={opponent?.photoUrl} />
        </View>
        <StatGrid stats={[
          { value: String(holes.length || 9), label: t.holesStat },
          {
            value: courseName,
            label: t.courseStat,
            compact: true,
            numberOfLines: 2,
            valueFontSize: courseStatFontSize(courseName),
          },
          {
            value: match.resultLabel ?? resultCode,
            label: t.resultStat,
            highlight: resultCode === 'W' ? 'win' : resultCode === 'L' ? 'loss' : undefined,
          },
        ]} />
        {me?.ratingDelta != null ? (
          <Text style={styles.muted}>
            {t.ratingDeltaStat} {formatRatingChange(me.ratingDelta)}
            {typeof me.eloAfter === 'number' ? ` · ${me.eloAfter}` : ''}
          </Text>
        ) : null}
      </View>
      <Text style={styles.sectionTitle}>{t.scorecardSection}</Text>
      <View style={styles.scorecardTable}>
        <ScorecardRow
          label={t.parStat}
          values={holes.map(hole => hole.par)}
          compact={holes.length > 9}
        />
        <ScorecardRow
          label={t.youStat}
          values={holes.map(hole => me?.holeStats.find(stat => stat.holeNumber === hole.number)?.strokes ?? '-')}
          accent
          compact={holes.length > 9}
        />
        <ScorecardRow
          label={t.oppStat}
          values={holes.map(hole => opponent?.holeStats.find(stat => stat.holeNumber === hole.number)?.strokes ?? '-')}
          compact={holes.length > 9}
        />
      </View>
      {footer}
    </View>
  );
}

function MatchResultScreen({
  t,
  match,
  token,
  setScreen,
  notificationCount,
  onOpenHistory,
  onStatsSaved,
}: {
  t: typeof dictionary.en;
  match: ActiveMatch | null;
  token?: string;
  setScreen: (screen: Screen) => void;
  notificationCount: number;
  onOpenHistory: (matchId: string) => void;
  onStatsSaved: () => Promise<BootstrapData | undefined>;
}) {
  if (!match) {
    return (
      <View>
        <AppScreenTitle title={t.matchResult} setScreen={setScreen} notificationCount={notificationCount} />
        <Text style={styles.muted}>{t.noCompletedMatch}</Text>
      </View>
    );
  }

  return (
    <CompletedMatchView
      t={t}
      match={match}
      title={t.matchResult}
      setScreen={setScreen}
      notificationCount={notificationCount}
      footer={(
        <View style={styles.matchResultActions}>
          <PostRoundStatsForm t={t} match={match} token={token} onSaved={onStatsSaved} />
          <TouchableOpacity style={styles.primaryButton} onPress={() => onOpenHistory(match.id)}>
            <Text style={styles.primaryButtonText}>{t.viewMatchHistory}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.secondaryMiniButton} onPress={() => setScreen('main')}>
            <Text style={styles.secondaryButtonText}>{t.backToHome}</Text>
          </TouchableOpacity>
        </View>
      )}
    />
  );
}

type PostRoundStatDraft = {
  putts?: string;
  fairwayHit?: boolean;
  penalties?: string;
};

function PostRoundStatsForm({
  t,
  match,
  token,
  onSaved,
}: {
  t: typeof dictionary.en;
  match: ActiveMatch;
  token?: string;
  onSaved: () => Promise<BootstrapData | undefined>;
}) {
  const holes = buildMatchHoles(match.course, match.holesToPlay);
  const [expanded, setExpanded] = useState(false);
  const [holeIndex, setHoleIndex] = useState(0);
  const [drafts, setDrafts] = useState<Record<number, PostRoundStatDraft>>({});
  const [busy, setBusy] = useState(false);
  const currentHole = holes[holeIndex];
  const current = currentHole ? drafts[currentHole.number] ?? {} : {};

  function updateCurrent(patch: Partial<PostRoundStatDraft>) {
    if (!currentHole) return;
    setDrafts(existing => ({
      ...existing,
      [currentHole.number]: { ...existing[currentHole.number], ...patch },
    }));
  }

  async function saveStatistics() {
    if (!token) return;
    const entries = Object.entries(drafts).map(([holeNumber, entry]) => ({
      holeNumber: Number(holeNumber),
      putts: entry.putts === '' || entry.putts === undefined ? null : Number(entry.putts),
      fairwayHit: entry.fairwayHit,
      penalties: entry.penalties === '' || entry.penalties === undefined ? null : Number(entry.penalties),
    }));
    setBusy(true);
    try {
      await apiPost(`/matchplays/${match.id}/statistics`, { entries }, token);
      await onSaved();
      Alert.alert(t.appName, t.statisticsSaved);
      setExpanded(false);
    } catch (error: any) {
      Alert.alert(t.appName, error?.message ?? t.couldNotSaveStatistics);
    } finally {
      setBusy(false);
    }
  }

  if (!expanded) {
    return (
      <TouchableOpacity style={styles.secondaryMiniButton} onPress={() => setExpanded(true)}>
        <Text style={styles.secondaryButtonText}>{t.addRoundStatistics}</Text>
      </TouchableOpacity>
    );
  }

  return (
    <View style={styles.moduleCard}>
      <Text style={styles.cardTitle}>{t.addRoundStatistics}</Text>
      <Text style={styles.muted}>{t.roundStatisticsHelp}</Text>
      <Text style={styles.sectionTitle}>
        {currentHole ? formatMessage(t.holeParLabel, { hole: matchHoleLabel(currentHole).toUpperCase(), par: currentHole.par }) : ''}
      </Text>
      <TextInput
        style={styles.searchInput}
        value={current.putts ?? ''}
        onChangeText={value => updateCurrent({ putts: value })}
        keyboardType="number-pad"
        placeholder={t.puttsShort}
      />
      {(currentHole?.par ?? 0) >= 4 ? (
        <SettingsRow
          label={t.fairwayHit}
          value={<Switch value={current.fairwayHit ?? false} onValueChange={value => updateCurrent({ fairwayHit: value })} />}
        />
      ) : null}
      <TextInput
        style={styles.searchInput}
        value={current.penalties ?? ''}
        onChangeText={value => updateCurrent({ penalties: value })}
        keyboardType="number-pad"
        placeholder={t.penaltiesLabel}
      />
      <View style={styles.row}>
        <TouchableOpacity
          style={styles.secondaryMiniButton}
          disabled={holeIndex === 0 || busy}
          onPress={() => setHoleIndex(index => Math.max(0, index - 1))}
        >
          <Text style={styles.secondaryButtonText}>{t.prevButton}</Text>
        </TouchableOpacity>
        {holeIndex < holes.length - 1 ? (
          <TouchableOpacity
            style={styles.pillButton}
            disabled={busy}
            onPress={() => setHoleIndex(index => Math.min(holes.length - 1, index + 1))}
          >
            <Text style={styles.pillButtonText}>{t.nextHole}</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity style={styles.primaryButton} disabled={busy} onPress={saveStatistics}>
            <Text style={styles.primaryButtonText}>{t.saveRoundStatistics}</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

function MatchHistoryScreen({
  t,
  match,
  setScreen,
  notificationCount,
  onBack,
}: {
  t: typeof dictionary.en;
  match: ActiveMatch | null;
  setScreen: (screen: Screen) => void;
  notificationCount: number;
  onBack: () => void;
}) {
  if (!match) {
    return (
      <View>
        <AppScreenTitle title={t.matchHistory} setScreen={setScreen} notificationCount={notificationCount} />
        <Text style={styles.muted}>{t.matchNotFound}</Text>
      </View>
    );
  }

  return (
    <CompletedMatchView
      t={t}
      match={match}
      title={t.matchHistory}
      setScreen={setScreen}
      notificationCount={notificationCount}
      footer={(
        <TouchableOpacity style={styles.primaryButton} onPress={onBack}>
          <Text style={styles.primaryButtonText}>{t.back}</Text>
        </TouchableOpacity>
      )}
    />
  );
}

function ProfileScreen({
  t,
  data,
  token,
  setScreen,
  notificationCount,
  onOpenMatch,
  onAvatarUpdated,
}: {
  t: typeof dictionary.en;
  data: BootstrapData;
  token?: string;
  setScreen: (screen: Screen) => void;
  notificationCount: number;
  onOpenMatch: (matchId: string) => void;
  onAvatarUpdated: () => Promise<BootstrapData | undefined>;
}) {
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarPreviewUri, setAvatarPreviewUri] = useState<string | null>(null);
  const [avatarStatus, setAvatarStatus] = useState<'hint' | 'error' | 'ok'>('hint');
  const recentMatches = data.recentMatches ?? [];
  const profileStats = data.profileStats;

  async function pickAndUploadAvatar() {
    if (!token || uploadingAvatar) {
      return;
    }

    const pickerResult = await launchImageLibrary({
      mediaType: 'photo',
      quality: 0.85,
      maxWidth: 1024,
      maxHeight: 1024,
      selectionLimit: 1,
    });

    if (pickerResult.didCancel || !pickerResult.assets?.[0]?.uri) {
      return;
    }

    const asset = pickerResult.assets[0];
    const localUri = asset.uri;
    setAvatarPreviewUri(localUri);
    setUploadingAvatar(true);
    setAvatarStatus('hint');
    try {
      const uploadResult = await apiUploadAvatar(token, {
        uri: localUri,
        type: asset.type ?? 'image/jpeg',
        name: asset.fileName ?? 'avatar.jpg',
        file: 'file' in asset ? (asset as { file?: Blob }).file : undefined,
      });

      if (!uploadResult?.photoUrl) {
        setAvatarPreviewUri(null);
        setAvatarStatus('error');
        return;
      }

      setAvatarPreviewUri(uploadResult.photoUrl);
      await onAvatarUpdated();
      setAvatarPreviewUri(null);
      setAvatarStatus('ok');
    } catch {
      setAvatarPreviewUri(null);
      setAvatarStatus('error');
    } finally {
      setUploadingAvatar(false);
    }
  }

  return (
    <View>
      <ScreenTitleRow
        title={t.profile}
        notificationCount={notificationCount}
        onPressNotifications={() => setScreen('notifications')}
      />
      <View style={styles.profileHeroCard}>
        <View style={styles.profileHeroTop}>
          <Avatar
            large
            initials={data.player.initials}
            photoUrl={avatarPreviewUri ?? data.player.photoUrl}
            uploading={uploadingAvatar}
            onPress={token ? () => void pickAndUploadAvatar() : undefined}
          />
          <View style={styles.profileHeroCopy}>
            <Text style={styles.profileHeroName}>{data.player.name}</Text>
            <Text style={styles.profileHeroClub}>{data.player.club}</Text>
            {token ? (
              <Text
                numberOfLines={3}
                style={
                  uploadingAvatar || avatarStatus === 'hint'
                    ? styles.avatarEditHint
                    : avatarStatus === 'error'
                      ? styles.avatarStatusError
                      : styles.avatarStatusOk
                }
              >
                {uploadingAvatar
                  ? t.uploadingPhoto
                  : avatarStatus === 'error'
                    ? t.couldNotUploadAvatar
                    : avatarStatus === 'ok'
                      ? t.photoUpdated
                      : t.tapPhotoToChange}
              </Text>
            ) : null}
          </View>
        </View>
        <View style={styles.profileHeroPills}>
          <OutlinePill label={`${t.rankPrefix}${data.player.rank}`} />
          <OutlinePill label={`${t.hcpShort} ${formatHandicapIndex(resolveHandicapIndex(data.player))}`} />
          {data.player.provisional ? <OutlinePill label={t.provisionalBadge} /> : null}
          <PremiumPill label={t.premium} />
        </View>
      </View>
      <View style={styles.profileStatsBar}>
        {(() => {
          const record = matchWinsLosses(data.player);
          return [
            { value: String(data.player.elo), label: t.ratingStat },
            { value: formatRatingChange(data.player.change), label: t.ratingChangeStat },
            { value: `${record.wins}/${record.losses}/${record.draws}`, label: t.recordStat },
            { value: `${data.player.winRate}%`, label: t.winRateStat },
          ];
        })().map((item, index) => (
          <View key={item.label} style={styles.profileStat}>
            {index > 0 ? <View style={styles.profileStatDivider} /> : null}
            <View style={styles.profileStatInner}>
              <Text style={styles.profileStatValue}>{item.value}</Text>
              <Text style={styles.profileStatLabel}>{item.label}</Text>
            </View>
          </View>
        ))}
      </View>
      <Text style={styles.sectionTitle}>{t.recentResults}</Text>
      {recentMatches.length > 0 ? (
        <>
          <View style={styles.resultChips}>
            {recentMatches.map(item => {
              const resultStyles = matchResultStyles(item.result);
              const label = normalizeMatchResult(item.result);
              return (
                <TouchableOpacity
                  key={item.id}
                  style={[styles.resultChip, resultStyles.chip]}
                  onPress={() => onOpenMatch(item.id)}
                >
                  <Text style={[styles.resultChipText, resultStyles.chipText]}>{label}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
          {recentMatches.map(item => {
            const resultStyles = matchResultStyles(item.result);
            const label = normalizeMatchResult(item.result);
            return (
              <TouchableOpacity key={`${item.id}-row`} style={styles.profileMatchRow} onPress={() => onOpenMatch(item.id)}>
                <Avatar initials={item.opponentInitials} photoUrl={item.opponentPhotoUrl} small />
                <View style={styles.rowContent}>
                  <Text style={styles.cardTitle}>{item.courseName}</Text>
                  <Text style={styles.muted}>vs {item.opponentName} · {item.myScore} - {item.opponentScore}</Text>
                </View>
                <View style={[styles.resultRowBadge, resultStyles.chip]}>
                  <Text style={[styles.resultChipText, resultStyles.chipText]}>{label}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </>
      ) : (
        <Text style={styles.muted}>{t.noCompletedMatchesYet}</Text>
      )}
      <Text style={styles.sectionTitle}>{t.performance}</Text>
      <Performance label={t.drivingAccuracy} value={profileStats?.drivingAccuracy != null ? `${profileStats.drivingAccuracy}%` : '—'} />
      <Performance label={t.greens} value={profileStats?.greensInRegulation != null ? `${profileStats.greensInRegulation}%` : '—'} />
      <Performance label={t.putts} value={profileStats?.averagePuttsPerRound != null ? String(profileStats.averagePuttsPerRound) : '—'} />
    </View>
  );
}

function GolfClubScreen({ t, data, setScreen, notificationCount, action }: ScreenProps) {
  return (
    <View>
      <AppScreenTitle title={t.golfClub} setScreen={setScreen} notificationCount={notificationCount} />
      {data.golfClubs.map(club => (
        <View key={club.id} style={styles.moduleCard}>
          <CoverPhoto uri={club.imageUrl} name={club.name} variant="club" />
          <Text style={styles.cardTitle}>{club.name}</Text>
          <Text style={styles.muted}>{club.address} · {club.slots}</Text>
          <TouchableOpacity style={styles.pillButton} onPress={() => action('/bookings', club, t.teeTimeBookingSent)}>
            <Text style={styles.pillButtonText}>{t.book}</Text>
          </TouchableOpacity>
        </View>
      ))}
      <Text style={styles.sectionTitle}>{t.friendJoinRequests}</Text>
      {data.notifications.map(note => (
        <View key={note.id} style={styles.moduleCard}>
          <Text style={styles.cardTitle}>{note.title}</Text>
          <Text style={styles.muted}>{note.body}</Text>
          <View style={styles.row}>
            <TouchableOpacity style={styles.pillButton} onPress={() => action('/join-requests', { bookingId: 'bk1', requesterId: 'u2' }, t.joinRequestAccepted)}>
              <Text style={styles.pillButtonText}>{t.confirm}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.secondaryMiniButton} onPress={() => action('/join-requests/jr1/rejected', {}, t.joinRequestDeclined)}>
              <Text style={styles.secondaryButtonText}>{t.decline}</Text>
            </TouchableOpacity>
          </View>
        </View>
      ))}
    </View>
  );
}

function AcademyScreen({ t, data, setScreen, notificationCount, action }: ScreenProps) {
  return (
    <View>
      <AppScreenTitle title={t.academy} setScreen={setScreen} notificationCount={notificationCount} />
      <ListModule rows={data.coaches.map(c => ({ title: c.name, meta: `${c.specialty} · ${c.price}`, action: t.requestLesson }))} onPress={(row) => action('/lessons', row, t.lessonRequestSent)} />
    </View>
  );
}

function PartnersScreen({ t, data, setScreen, notificationCount, action }: ScreenProps) {
  return (
    <View>
      <AppScreenTitle title={t.partners} setScreen={setScreen} notificationCount={notificationCount} />
      <ListModule rows={data.partners.map(p => ({ title: p.name, meta: `${p.category} · ${t.cashbackWord} ${p.cashback}`, action: t.useOffer }))} onPress={(row) => action('/cashback/usage', row, t.cashbackUsageRecorded)} />
    </View>
  );
}

function TournamentScreen({ t, data, action, setScreen, notificationCount }: ScreenProps) {
  return (
    <View>
      <AppScreenTitle title={t.tournaments} setScreen={setScreen} notificationCount={notificationCount} />
      {data.tournaments.map(tournament => (
        <View key={tournament.id} style={styles.moduleCard}>
          {tournament.videoUrl || tournament.imageUrl ? (
            <View style={styles.eventImage}>
              {tournament.videoUrl ? (
                <FeaturedEventVideo uri={tournament.videoUrl} />
              ) : (
                <Image source={{ uri: tournament.imageUrl! }} style={styles.featureCardMediaFill} resizeMode="cover" />
              )}
            </View>
          ) : null}
          <Text style={styles.cardTitle}>{tournament.title}</Text>
          <Text style={styles.muted}>{tournament.fieldName} · {tournament.date} {tournament.time}</Text>
          <Text style={styles.muted}>{tournament.forWhom} · {tournament.cost} · {tournament.format} · {tournament.tees}</Text>
          <View style={styles.row}>
            <TouchableOpacity style={styles.pillButton} onPress={() => action('/tournaments/apply', { tournamentId: tournament.id }, t.tournamentApplicationSent)}>
              <Text style={styles.pillButtonText}>{t.apply}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.pillButton} onPress={() => setScreen('scoreInput')}>
              <Text style={styles.pillButtonText}>{t.startButton}</Text>
            </TouchableOpacity>
          </View>
        </View>
      ))}
    </View>
  );
}

function MatchplaySetupScreen({
  t,
  data,
  setScreen,
  notificationCount,
  onChooseCourse,
}: {
  t: typeof dictionary.en;
  data: BootstrapData;
  setScreen: (screen: Screen) => void;
  notificationCount: number;
  onChooseCourse: () => void;
}) {
  const opponent = data.friends?.[0];
  const recentCourse = data.courses?.find(course => course.id === data.recentCourseIds?.[0]);
  const defaultCourse = recentCourse ?? data.courses?.[0];
  const defaultHoleCount = defaultCourse ? courseHoleCount(defaultCourse) : null;
  return (
    <View>
      <AppScreenTitle title={t.matchplaySetupTitle} setScreen={setScreen} notificationCount={notificationCount} />
      <View style={styles.moduleCard}>
        <View style={styles.selectedCourseHeader}>
          <Text style={styles.cardTitle}>{t.suggestedCourse}</Text>
          {defaultHoleCount ? <Text style={styles.selectedHoleBadge}>{formatMessage(t.holesCount, { count: defaultHoleCount })}</Text> : null}
        </View>
        <Text style={styles.muted}>
          {defaultCourse
            ? `${defaultCourse.name} · ${defaultCourse.city} · Par ${defaultCourse.par}`
            : t.coursesAfterSignIn}
        </Text>
        {defaultCourse && defaultHoleCount ? (
          <Text style={styles.courseOptionHint}>
            {formatMessage(t.youWillScoreHoles, { holes: defaultHoleCount })}
          </Text>
        ) : null}
      </View>
      <View style={styles.moduleCard}>
        <Text style={styles.cardTitle}>{t.opponentSection}</Text>
        <Text style={styles.muted}>{opponent ? `${opponent.name} · ${t.handicapShort} ${opponent.handicap}` : t.noFriendSelected}</Text>
      </View>
      <TouchableOpacity style={styles.primaryButton} onPress={onChooseCourse} disabled={!opponent}>
        <Text style={styles.primaryButtonText}>{t.chooseCourse}</Text>
      </TouchableOpacity>
    </View>
  );
}

function UserProfileScreen({
  t,
  profile,
  loading,
  setScreen,
  notificationCount,
}: {
  t: typeof dictionary.en;
  profile?: any;
  loading?: boolean;
  setScreen: (screen: Screen) => void;
  notificationCount: number;
}) {
  if (loading) {
    return (
      <View>
        <AppScreenTitle title={t.loadingProfile} setScreen={setScreen} notificationCount={notificationCount} />
      </View>
    );
  }
  if (!profile) {
    return (
      <View>
        <AppScreenTitle title={t.playerProfile} setScreen={setScreen} notificationCount={notificationCount} />
      </View>
    );
  }
  return (
    <View>
      <AppScreenTitle title={t.playerProfile} setScreen={setScreen} notificationCount={notificationCount} />
      <View style={styles.profileHeroCard}>
        <View style={styles.profileHeroTop}>
          <Avatar large initials={profile.initials} photoUrl={profile.photoUrl} />
          <View style={styles.profileHeroCopy}>
            <Text style={styles.profileHeroName}>{profile.name}</Text>
            {profile.handle ? <Text style={styles.profileHeroClub}>{profileHandleLabel(profile.handle)}</Text> : null}
            <Text style={styles.profileHeroClub}>{profile.homeClub} · {profile.city}</Text>
          </View>
        </View>
        <View style={styles.profileHeroPills}>
          <OutlinePill label={`${t.ratingLabel} ${profile.elo}`} />
          <OutlinePill label={`${t.hcpShort} ${formatHandicapIndex(resolveHandicapIndex(profile))}`} />
          {profile.provisional ? <OutlinePill label={t.provisionalBadge} /> : null}
        </View>
      </View>
      <View style={styles.profileStatsBar}>
        {(() => {
          const record = matchWinsLosses(profile);
          return [
            { value: String(profile.matches ?? 0), label: t.matchesLabel },
            { value: `${record.wins}/${record.losses}/${record.draws}`, label: t.recordStat },
            { value: `${profile.winRate ?? 0}%`, label: t.winRateStat },
          ];
        })().map((item, index) => (
          <View key={item.label} style={styles.profileStat}>
            {index > 0 ? <View style={styles.profileStatDivider} /> : null}
            <View style={styles.profileStatInner}>
              <Text style={styles.profileStatValue}>{item.value}</Text>
              <Text style={styles.profileStatLabel}>{item.label}</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

function CoverPhoto({
  uri,
  name,
  variant = 'club',
}: {
  uri?: string | null;
  name: string;
  variant?: 'club' | 'course';
}) {
  const [failed, setFailed] = useState(false);
  return (
    <View style={variant === 'course' ? styles.courseOptionPhoto : styles.golfClubPhoto}>
      {uri && !failed ? (
        <Image
          source={{ uri }}
          style={styles.coverPhotoImage}
          resizeMode="cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <Text style={styles.coverPhotoFallback}>{communityInitials(name)}</Text>
      )}
    </View>
  );
}

function communityInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function CommunityLogo({ name, logoUrl }: { name: string; logoUrl?: string | null }) {
  const [failed, setFailed] = useState(false);
  return (
    <View style={styles.communityLogo}>
      {logoUrl && !failed ? (
        <Image
          source={{ uri: logoUrl }}
          style={styles.communityLogoImage}
          resizeMode="cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <Text style={styles.communityLogoText}>{communityInitials(name)}</Text>
      )}
    </View>
  );
}

function CommunitiesScreen({
  t,
  data,
  setScreen,
  notificationCount,
  action,
}: ScreenProps) {
  return (
    <View>
      <AppScreenTitle title={t.communities} setScreen={setScreen} notificationCount={notificationCount} />
      {data.communities.map(community => (
        <View key={community.id} style={styles.moduleCard}>
          <View style={styles.communityHeader}>
            <CommunityLogo name={community.name} logoUrl={community.logoUrl} />
            <View style={styles.communityHeaderText}>
              <Text style={styles.cardTitle}>{community.name}</Text>
              <Text style={styles.muted}>{community.members} {t.membersCount} · {community.status}</Text>
            </View>
          </View>
          <TouchableOpacity style={styles.pillButton} onPress={() => action('/communities/apply', { communityId: community.id }, t.communityApplicationSent)}>
            <Text style={styles.pillButtonText}>{t.joinButton}</Text>
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );
}

function EventsScreen({
  t,
  data,
  setScreen,
  notificationCount,
  action,
}: ScreenProps) {
  const events = data.events ?? [];
  return (
    <View>
      <AppScreenTitle title={t.eventsLabel} setScreen={setScreen} notificationCount={notificationCount} />
      {events.map(event => (
        <View key={event.id} style={styles.moduleCard}>
          {event.imageUrl ? <Image source={{ uri: event.imageUrl }} style={styles.eventImage} resizeMode="cover" /> : null}
          <Text style={styles.cardTitle}>{event.title}</Text>
          <Text style={styles.muted}>{event.location} · {event.price ?? t.free} · {event.status}</Text>
          <Text style={styles.muted}>{event.description}</Text>
          <TouchableOpacity style={styles.pillButton} onPress={() => action(`/events/${event.id}/signup`, {}, t.eventSignupSent)}>
            <Text style={styles.pillButtonText}>{t.signUpButton}</Text>
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );
}

function SettingsScreen({
  t,
  data,
  language,
  setLanguage,
  setScreen,
  notificationCount,
  onSignOut,
}: {
  t: typeof dictionary.en;
  data: BootstrapData;
  language: Language;
  setLanguage: (language: Language) => void | Promise<void>;
  setScreen: (screen: Screen) => void;
  notificationCount: number;
  onSignOut: () => void;
}) {
  const [alerts, setAlerts] = useState(true);
  const player = data.player;
  const privacyLabel = player.profileVisibility === 'public' ? t.privacyPublic : t.privacyFriendsOnly;
  const membershipLabel = player.membershipStatus
    ? player.membershipStatus.charAt(0).toUpperCase() + player.membershipStatus.slice(1)
    : t.memberLabel;

  return (
    <View>
      <ScreenTitleRow
        title={t.settings}
        notificationCount={notificationCount}
        onPressNotifications={() => setScreen('notifications')}
      />
      <View style={styles.settingsGroup}>
        <SettingsRow label={t.fieldFullName} value={player.name} />
        <SettingsRow label={t.fieldClub} value={player.club} />
        <SettingsRow label={t.fieldCity} value={player.city ?? '—'} />
        <SettingsRow label={t.fieldCountry} value={player.country ?? '—'} />
        <SettingsRow label={t.fieldEmail} value={player.email ?? '—'} />
      </View>
      <View style={styles.settingsGroup}>
        <View style={styles.settingsRow}><Text style={styles.cardTitle}>{t.pushAlerts}</Text><Switch value={alerts} onValueChange={setAlerts} trackColor={{ true: colors.purple }} /></View>
        <TouchableOpacity style={styles.settingsRow} onPress={() => void setLanguage(language === 'en' ? 'ru' : 'en')}>
          <Text style={styles.cardTitle}>{t.language}</Text><Text style={styles.points}>{language.toUpperCase()}</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.settingsGroup}>
        <SettingsRow label={t.privacy} value={privacyLabel} />
        <SettingsRow label={t.subscription} value={membershipLabel} />
        <TouchableOpacity style={styles.settingsRow} onPress={() => void Linking.openURL(`mailto:${t.supportEmail}`)}>
          <Text style={styles.cardTitle}>{t.support}</Text>
          <Text style={styles.points}>{t.supportEmail}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={onSignOut}>
          <Text style={styles.danger}>{t.signOut}</Text>
        </TouchableOpacity>
      </View>
      {Platform.OS === 'web' && <PwaInstallInstructions t={t} />}
    </View>
  );
}

function ListModule({ rows, onPress }: { rows: { title: string; meta: string; action: string }[]; onPress: (row: object) => void }) {
  return (
    <View>
      {rows.map(row => (
        <View key={row.title} style={styles.moduleCard}>
          <Text style={styles.cardTitle}>{row.title}</Text>
          <Text style={styles.muted}>{row.meta}</Text>
          <TouchableOpacity style={styles.pillButton} onPress={() => onPress(row)}>
            <Text style={styles.pillButtonText}>{row.action}</Text>
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );
}

export default App;
